import { supabase } from "./supabaseClient";
import { useState, useEffect } from "react";
import { useToast, ToastContainer, ConfirmDialog } from "./toast";

const SERVER = "https://legisim-server.onrender.com";

function displayStatus(s) {
  const MAP = {
    "Introduced":"Introduced","Engrossed":"In Progress","Enrolled":"PA Signed",
    "Chaptered":"PA Signed","Passed":"Passed","Failed":"Failed","Vetoed":"Vetoed",
    "Report Pass":"In Committee","Report Fail":"In Committee","Refer":"In Committee",
    "Override":"Passed","Draft":"Draft",
  };
  if (!s || typeof s === "number") return "Introduced";
  return MAP[s] || s;
}

async function handleSignOut(supabaseClient, onNavigate) {
  await supabaseClient.auth.signOut();
  onNavigate("home");
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{height:100%;}
body{background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;}
.page{display:flex;flex-direction:column;height:100vh;overflow:hidden;background:var(--bg);}

/* MASTHEAD */
.legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
.legisim-masthead::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent 0%,var(--gold) 30%,#e8c96a 50%,var(--gold) 70%,transparent 100%);}
.masthead-left{display:flex;align-items:center;gap:1rem;z-index:1;}
.masthead-right{display:flex;align-items:center;gap:0.6rem;z-index:1;}
.masthead-wordmark{font-family:var(--font-display);font-size:1.35rem;font-weight:900;letter-spacing:-0.03em;color:var(--text);line-height:1;cursor:pointer;}
.masthead-wordmark em{font-style:italic;color:var(--gold);}
.masthead-wordmark sup{font-family:var(--font-mono);font-size:0.42rem;color:var(--muted);vertical-align:super;}
.mast-back{background:transparent;border:2px solid var(--gold-dim);border-radius:8px;padding:0.45rem 1.1rem;color:var(--gold);font-family:var(--font-mono);font-size:0.72rem;font-weight:600;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-back:hover{border-color:var(--gold);background:var(--gold-glow);}
.mast-btn-sm{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.4rem 1rem;color:var(--muted);font-family:var(--font-mono);font-size:0.7rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;white-space:nowrap;}
.mast-btn-sm:hover{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.5);color:var(--red);}

/* LAYOUT */
.cp-body{flex:1;display:flex;overflow:hidden;min-height:0;}

/* SIDEBAR */
.cp-sidebar{width:260px;flex-shrink:0;background:var(--surface);border-right:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden;}
.cp-sidebar-head{padding:1.1rem 1rem 0.75rem;border-bottom:1px solid var(--border);flex-shrink:0;}
.cp-sidebar-title{font-family:var(--font-display);font-size:1.15rem;font-weight:900;letter-spacing:-0.02em;color:var(--text);line-height:1;margin-bottom:0.6rem;}
.cp-sidebar-title em{font-style:italic;color:var(--gold);}
.cp-add-btn{width:100%;padding:0.5rem;background:rgba(201,168,76,0.08);border:1px solid var(--gold-dim);border-radius:8px;font-family:var(--font-mono);font-size:0.65rem;color:var(--gold);cursor:pointer;letter-spacing:0.06em;text-align:center;transition:all 0.15s;}
.cp-add-btn:hover{background:rgba(201,168,76,0.14);}
.cp-search{display:block;width:calc(100% - 1.25rem);margin:0.65rem 0.625rem 0.35rem;background:var(--surface2);border:1px solid var(--border);border-radius:7px;padding:0.4rem 0.75rem;font-family:var(--font-mono);font-size:0.65rem;color:var(--text);outline:none;}
.cp-search::placeholder{color:var(--muted);}
.cp-client-list{flex:1;overflow-y:auto;padding:0.25rem 0.5rem 0.5rem;scrollbar-width:thin;}
.cp-client-item{display:flex;align-items:center;gap:0.6rem;padding:0.6rem 0.5rem;border-radius:8px;cursor:pointer;transition:background 0.15s;border:1px solid transparent;margin-bottom:2px;}
.cp-client-item:hover{background:var(--surface2);}
.cp-client-item.active{background:rgba(201,168,76,0.08);border-color:var(--gold-dim);}
.cp-avatar{width:34px;height:34px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-family:var(--font-mono);font-size:0.62rem;font-weight:600;flex-shrink:0;border:1px solid;overflow:hidden;}
.cp-avatar img{width:100%;height:100%;object-fit:cover;border-radius:7px;}
.cp-client-info{flex:1;min-width:0;}
.cp-client-name{font-size:0.8rem;font-weight:500;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.cp-client-sub{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.cp-priority-dot{width:6px;height:6px;border-radius:50%;flex-shrink:0;}
.dot-high{background:#c0392b;}
.dot-normal{background:var(--gold);}
.dot-low{background:var(--muted);}

/* MAIN */
.cp-main{flex:1;display:flex;flex-direction:column;overflow:hidden;min-height:0;}

/* BACK BUTTON */
.cp-back-btn{display:inline-flex;align-items:center;gap:0.4rem;background:transparent;border:1px solid var(--border);border-radius:7px;padding:0.35rem 0.75rem;cursor:pointer;transition:all 0.18s;flex-shrink:0;align-self:flex-start;margin-bottom:0.75rem;}
.cp-back-btn:hover{border-color:var(--gold-dim);background:var(--gold-glow);}
.cp-back-btn-arrow{font-size:0.9rem;color:var(--muted);line-height:1;}
.cp-back-btn-text{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);letter-spacing:0.04em;white-space:nowrap;}
.cp-back-btn:hover .cp-back-btn-arrow,.cp-back-btn:hover .cp-back-btn-text{color:var(--text);}
[data-theme="light"] .cp-back-btn{border-color:rgba(26,39,68,0.15);}
[data-theme="light"] .cp-back-btn:hover{background:rgba(26,39,68,0.05);}

/* CLIENT HEADER */
.cp-header{flex-shrink:0;background:var(--surface);border-bottom:1px solid var(--border);padding:1.25rem 1.75rem 0;position:relative;}
.cp-header::before{content:'';position:absolute;top:0;left:8%;right:8%;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent);}
.cp-header-top{display:flex;align-items:flex-start;gap:1rem;margin-bottom:0.9rem;}
.cp-big-avatar{width:50px;height:50px;border-radius:12px;display:flex;align-items:center;justify-content:center;font-family:var(--font-mono);font-size:1rem;font-weight:700;flex-shrink:0;border:1px solid;overflow:hidden;}
.cp-big-avatar img{width:100%;height:100%;object-fit:cover;border-radius:11px;}
.cp-header-info{flex:1;min-width:0;}
.cp-big-name{font-family:var(--font-display);font-size:1.5rem;font-weight:900;letter-spacing:-0.02em;color:var(--text);line-height:1;}
.cp-big-sub{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);letter-spacing:0.07em;margin-top:0.3rem;}
.cp-badges{display:flex;gap:0.35rem;flex-wrap:wrap;margin-top:0.45rem;}
.cp-badge{display:inline-flex;align-items:center;padding:0.18rem 0.55rem;border-radius:20px;font-family:var(--font-mono);font-size:0.55rem;letter-spacing:0.06em;border:1px solid;}
.badge-high{background:rgba(192,57,43,0.1);border-color:rgba(192,57,43,0.3);color:#e74c3c;}
.badge-ind{background:var(--gold-glow);border-color:var(--gold-dim);color:var(--gold);}
.badge-active{background:rgba(46,125,82,0.1);border-color:rgba(46,125,82,0.3);color:#27ae60;}
.badge-neutral{background:var(--surface2);border-color:var(--border);color:var(--muted);}
.cp-header-actions{display:flex;gap:0.5rem;align-items:flex-start;flex-shrink:0;}
.cp-action-btn{padding:0.4rem 0.9rem;border-radius:8px;font-family:var(--font-mono);font-size:0.62rem;cursor:pointer;letter-spacing:0.05em;transition:all 0.15s;white-space:nowrap;}
.btn-gold{background:var(--gold);border:1px solid var(--gold);color:#09090e;font-weight:700;}
.btn-gold:hover{background:#d4b86a;}
.btn-outline{background:transparent;border:1px solid var(--border);color:var(--muted);}
.btn-outline:hover{border-color:var(--gold-dim);color:var(--gold);}
.btn-danger{background:transparent;border:1px solid rgba(192,57,43,0.4);color:var(--red);}
.btn-danger:hover{background:rgba(192,57,43,0.08);border-color:var(--red);}

/* STATS */
.cp-stats{display:flex;border-top:1px solid var(--border);}
.cp-stat{flex:1;padding:0.85rem 0;text-align:center;border-right:1px solid var(--border);}
.cp-stat:last-child{border-right:none;}
.cp-stat-num{font-family:var(--font-display);font-size:1.8rem;font-weight:900;color:var(--gold);line-height:1;}
.cp-stat-label{font-family:var(--font-body);font-size:0.72rem;color:var(--muted);margin-top:0.25rem;}

/* TABS */
.cp-tabs{display:flex;border-bottom:1px solid var(--border);background:var(--surface);flex-shrink:0;padding:0 1.75rem;}
.cp-tab{padding:0.75rem 1.1rem;font-family:var(--font-mono);font-size:0.7rem;color:var(--muted);cursor:pointer;letter-spacing:0.07em;border-bottom:2px solid transparent;transition:all 0.15s;white-space:nowrap;}
.cp-tab:hover{color:var(--text);}
.cp-tab.active{color:var(--gold);border-bottom-color:var(--gold);}

/* TAB CONTENT */
.cp-content{flex:1;overflow-y:auto;padding:1.25rem 1.75rem;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
.cp-grid{display:grid;grid-template-columns:1fr 1fr;gap:1.1rem;}
.cp-full{grid-column:1/-1;}

/* CARDS */
.cp-card{background:var(--surface);border:1px solid var(--border);border-radius:12px;overflow:hidden;}
.cp-card-head{padding:0.8rem 1.1rem;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.cp-card-title{font-family:var(--font-display);font-size:0.95rem;font-weight:900;letter-spacing:-0.01em;color:var(--text);}
.cp-card-action{font-family:var(--font-mono);font-size:0.62rem;color:var(--gold);cursor:pointer;border:1px solid var(--gold-dim);border-radius:5px;padding:0.22rem 0.65rem;background:var(--gold-glow);transition:all 0.15s;}
.cp-card-action:hover{background:rgba(201,168,76,0.15);}

/* BILL ROWS */
.cp-bill-row{display:flex;align-items:flex-start;gap:0.65rem;padding:0.65rem 1.1rem;border-bottom:1px solid var(--border);transition:background 0.12s;}
.cp-bill-row:last-child{border-bottom:none;}
.cp-bill-row:hover{background:var(--surface2);}
.cp-bill-num{font-family:var(--font-mono);font-size:0.75rem;color:var(--gold);font-weight:600;width:58px;flex-shrink:0;padding-top:2px;}
.cp-bill-info{flex:1;min-width:0;}
.cp-bill-title{font-size:0.88rem;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.cp-bill-nickname{font-family:var(--font-display);font-size:0.72rem;color:var(--gold);margin-top:0.15rem;font-style:italic;}
.cp-bill-meta{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);margin-top:0.15rem;}
.cp-bill-notes{font-size:0.72rem;color:var(--muted);margin-top:0.2rem;font-style:italic;}
.pos-tag{font-family:var(--font-mono);font-size:0.6rem;padding:0.18rem 0.55rem;border-radius:4px;flex-shrink:0;font-weight:600;}
.pos-support{background:rgba(46,125,82,0.12);color:#27ae60;border:1px solid rgba(46,125,82,0.25);}
.pos-oppose{background:rgba(192,57,43,0.1);color:#e74c3c;border:1px solid rgba(192,57,43,0.2);}
.pos-monitor{background:var(--gold-glow);color:var(--gold);border:1px solid var(--gold-dim);}
.pos-neutral{background:var(--surface2);color:var(--muted);border:1px solid var(--border);}
.moved-tag{font-family:var(--font-mono);font-size:0.5rem;padding:0.1rem 0.35rem;border-radius:4px;background:rgba(46,125,82,0.12);color:#27ae60;border:1px solid rgba(46,125,82,0.2);flex-shrink:0;}
.cp-row-actions{display:flex;gap:0.3rem;flex-shrink:0;align-items:flex-start;padding-top:1px;}
.cp-row-btn{background:transparent;border:1px solid var(--border);border-radius:5px;padding:0.15rem 0.45rem;font-family:var(--font-mono);font-size:0.52rem;color:var(--muted);cursor:pointer;transition:all 0.12s;}
.cp-row-btn:hover{border-color:var(--gold-dim);color:var(--gold);}
.cp-row-btn.del:hover{border-color:rgba(192,57,43,0.4);color:var(--red);}

/* MEETING ROWS */
.cp-meeting-row{display:flex;gap:0.75rem;padding:0.7rem 1.1rem;border-bottom:1px solid var(--border);}
.cp-meeting-row:last-child{border-bottom:none;}
.cp-meeting-date{width:38px;flex-shrink:0;text-align:center;}
.cp-meeting-month{font-family:var(--font-mono);font-size:0.55rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;}
.cp-meeting-day{font-family:var(--font-display);font-size:1.5rem;font-weight:900;color:var(--gold);line-height:1;}
.cp-meeting-info{flex:1;min-width:0;}
.cp-meeting-title{font-size:0.9rem;color:var(--text);font-weight:500;}
.cp-meeting-sub{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);margin-top:0.2rem;}
.cp-meeting-followup{font-family:var(--font-mono);font-size:0.62rem;color:#27ae60;margin-top:0.25rem;}
.cp-meeting-past .cp-meeting-day{color:var(--muted);}
.cp-meeting-past .cp-meeting-title{color:var(--muted);}

/* ACTIVITY ROWS */
.cp-act-row{display:flex;align-items:center;gap:0.65rem;padding:0.55rem 1.1rem;border-bottom:1px solid var(--border);}
.cp-act-del{background:none;border:none;cursor:pointer;color:var(--muted);font-size:0.75rem;padding:0.15rem 0.35rem;border-radius:4px;transition:color 0.15s;flex-shrink:0;opacity:0.5;}
.cp-act-del:hover{color:#c0392b;opacity:1;}
.cp-bill-row{cursor:pointer;transition:background 0.15s;}
.cp-bill-row:hover{background:var(--surface2);}
.cp-meeting-row{cursor:pointer;transition:background 0.15s;}
.cp-meeting-row:hover{background:var(--surface2);}
.cp-detail-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.65);backdrop-filter:blur(4px);z-index:600;display:flex;align-items:center;justify-content:center;padding:1.5rem;}
.cp-detail-modal{background:var(--charcoal);border:1px solid var(--border);border-radius:16px;width:100%;max-width:600px;max-height:85vh;display:flex;flex-direction:column;overflow:hidden;}
.cp-detail-head{padding:1.5rem 1.75rem 1.1rem;border-bottom:1px solid var(--border);flex-shrink:0;}
.cp-detail-number{font-family:var(--font-mono);font-size:0.72rem;font-weight:600;color:var(--gold);letter-spacing:0.08em;margin-bottom:0.3rem;}
.cp-detail-title{font-size:1.05rem;font-weight:700;color:var(--text);line-height:1.4;margin-bottom:0.65rem;}
.cp-detail-body{flex:1;overflow-y:auto;padding:1.25rem 1.75rem;display:flex;flex-direction:column;gap:0.85rem;scrollbar-width:thin;}
.cp-detail-row{display:flex;flex-direction:column;gap:0.2rem;}
.cp-detail-label{font-family:var(--font-mono);font-size:0.55rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;}
.cp-detail-value{font-size:0.82rem;color:var(--text);line-height:1.5;}
.cp-detail-foot{padding:0.9rem 1.75rem;border-top:1px solid var(--border);display:flex;justify-content:space-between;align-items:center;gap:1rem;flex-shrink:0;}
[data-theme="light"] .cp-detail-modal{background:#f8f9fc;}
[data-theme="light"] .cp-detail-foot .cp-detail-action{color:#1a2744;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .cp-detail-foot .cp-detail-action:hover{color:#c9a84c;border-color:rgba(201,168,76,0.4);}
.cp-act-row:last-child{border-bottom:none;}
.cp-act-icon{width:32px;height:32px;border-radius:8px;display:flex;align-items:center;justify-content:center;font-size:0.85rem;flex-shrink:0;}
.act-meeting{background:rgba(59,111,212,0.12);color:#3b6fd4;}
.act-email{background:var(--gold-glow);color:var(--gold);}
.act-testimony{background:rgba(46,125,82,0.12);color:#27ae60;}
.act-research{background:rgba(155,89,182,0.12);color:#9b59b6;}
.act-call{background:rgba(52,152,219,0.12);color:#3498db;}
.cp-act-info{flex:1;min-width:0;}
.cp-act-desc{font-size:0.88rem;color:var(--text);}
.cp-act-meta{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);margin-top:0.15rem;}
.cp-act-hrs{font-family:var(--font-mono);font-size:0.72rem;color:var(--gold);font-weight:600;flex-shrink:0;}

/* NOTES TAB */
.cp-notes-tab{padding:1.1rem;}
.cp-note-entry{background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:0.9rem 1rem;margin-bottom:0.75rem;position:relative;}
.cp-note-entry:last-child{margin-bottom:0;}
.cp-note-date{font-family:var(--font-mono);font-size:0.52rem;color:var(--muted);letter-spacing:0.08em;margin-bottom:0.4rem;}
.cp-note-text{font-size:0.83rem;color:var(--text);line-height:1.7;white-space:pre-wrap;}
.cp-note-actions{display:flex;gap:0.3rem;margin-top:0.5rem;}
.cp-add-note-area{background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:1rem;}
.cp-add-note-area textarea{width:100%;background:transparent;border:none;outline:none;font-family:var(--font-body);font-size:0.83rem;color:var(--text);resize:vertical;min-height:80px;line-height:1.6;}
.cp-add-note-area textarea::placeholder{color:var(--muted);}
.cp-add-note-foot{display:flex;justify-content:flex-end;margin-top:0.5rem;}

/* CONTACT GRID */
.cp-contact-grid{display:grid;grid-template-columns:1fr 1fr;gap:0.85rem;padding:1rem 1.1rem;}
.cp-field label{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;display:block;margin-bottom:0.25rem;}
.cp-field value{font-size:0.88rem;color:var(--text);display:block;font-weight:500;}

/* REPORT CARD */
.cp-report-card{background:linear-gradient(135deg,rgba(201,168,76,0.06) 0%,var(--surface) 60%);border:1px solid var(--gold-dim);border-radius:12px;padding:1.4rem 1.5rem;display:flex;align-items:center;gap:1.25rem;}
.cp-report-icon{font-size:2.2rem;flex-shrink:0;}
.cp-report-info{flex:1;}
.cp-report-title{font-family:var(--font-display);font-size:1.15rem;font-weight:900;color:var(--text);margin-bottom:0.35rem;}
.cp-report-desc{font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);line-height:1.6;}
.cp-report-btn{background:var(--gold);border:none;border-radius:8px;padding:0.55rem 1.25rem;font-family:var(--font-mono);font-size:0.65rem;color:#09090e;font-weight:700;cursor:pointer;flex-shrink:0;letter-spacing:0.05em;transition:all 0.15s;}
.cp-report-btn:hover{background:#d4b86a;}
.cp-report-btn:disabled{opacity:0.6;cursor:not-allowed;}

/* EMPTY STATE */
.cp-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:3rem;text-align:center;gap:0.75rem;}
.cp-empty-icon{font-size:2.5rem;opacity:0.4;}
.cp-empty-text{font-family:var(--font-mono);font-size:0.68rem;color:var(--muted);}

/* MODAL */
.cp-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.7);display:flex;align-items:center;justify-content:center;z-index:999;backdrop-filter:blur(4px);}
.cp-modal{background:var(--charcoal);border:1px solid var(--border);border-radius:16px;width:520px;max-height:85vh;display:flex;flex-direction:column;overflow:hidden;position:relative;}
.cp-modal::before{content:'';position:absolute;top:0;left:10%;right:10%;height:1px;background:linear-gradient(90deg,transparent,var(--gold),transparent);}
.cp-modal-head{padding:1.5rem 1.75rem 1rem;border-bottom:1px solid var(--border);}
.cp-modal-title{font-family:var(--font-display);font-size:1.3rem;font-weight:900;color:var(--text);}
.cp-modal-body{flex:1;overflow-y:auto;padding:1.25rem 1.75rem;display:flex;flex-direction:column;gap:0.85rem;}
.cp-modal-foot{padding:1rem 1.75rem;border-top:1px solid var(--border);display:flex;justify-content:flex-end;gap:0.65rem;}
.cp-form-group{display:flex;flex-direction:column;gap:0.3rem;}
.cp-form-row{display:grid;grid-template-columns:1fr 1fr;gap:0.75rem;}
.cp-label{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);letter-spacing:0.08em;text-transform:uppercase;}
.cp-input{background:var(--surface2);border:1px solid var(--border);border-radius:7px;padding:0.5rem 0.75rem;font-family:var(--font-mono);font-size:0.72rem;color:var(--text);outline:none;transition:border-color 0.15s;width:100%;}
.cp-input:focus{border-color:var(--gold-dim);}
.cp-select{background:var(--surface2);border:1px solid var(--border);border-radius:7px;padding:0.5rem 0.75rem;font-family:var(--font-mono);font-size:0.72rem;color:var(--text);outline:none;width:100%;cursor:pointer;}
.cp-textarea{background:var(--surface2);border:1px solid var(--border);border-radius:7px;padding:0.5rem 0.75rem;font-family:var(--font-body);font-size:0.8rem;color:var(--text);outline:none;resize:vertical;min-height:80px;width:100%;transition:border-color 0.15s;}
.cp-textarea:focus{border-color:var(--gold-dim);}
.btn-cancel{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.45rem 1rem;font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);cursor:pointer;}
.btn-save{background:var(--gold);border:1px solid var(--gold);border-radius:8px;padding:0.45rem 1.1rem;font-family:var(--font-mono);font-size:0.65rem;color:#09090e;font-weight:700;cursor:pointer;}

/* GENERATING OVERLAY */
.gen-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.8);display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1.5rem;z-index:9999;backdrop-filter:blur(6px);}
.gen-title{font-family:var(--font-display);font-size:1.8rem;font-weight:900;color:var(--gold);}
.gen-sub{font-family:var(--font-mono);font-size:0.7rem;color:var(--muted);}
.gen-dots{display:flex;gap:8px;}
.gen-dot{width:10px;height:10px;border-radius:50%;background:var(--gold);animation:gen-bounce 1.2s ease-in-out infinite;}
.gen-dot:nth-child(2){animation-delay:0.2s;}
.gen-dot:nth-child(3){animation-delay:0.4s;}
@keyframes gen-bounce{0%,80%,100%{transform:scale(0.6);opacity:0.4;}40%{transform:scale(1);opacity:1;}}

/* NO CLIENT SELECTED */
.cp-no-client{flex:1;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:1rem;padding:3rem;}
.cp-no-client-icon{font-size:3rem;opacity:0.3;}
.cp-no-client-title{font-family:var(--font-display);font-size:1.4rem;font-weight:700;color:var(--muted);}
.cp-no-client-sub{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);text-align:center;max-width:320px;line-height:1.6;}

/* LANDING DASHBOARD */
.cp-landing{flex:1;overflow:hidden;padding:1.5rem 2rem;display:flex;flex-direction:column;min-height:0;gap:1.25rem;}
.cp-landing-title{display:flex;align-items:baseline;gap:0.5rem;flex-shrink:0;}
.cp-landing-title-main{font-family:var(--font-display);font-size:1.75rem;font-weight:900;letter-spacing:-0.03em;color:var(--text);line-height:1;}
.cp-landing-title-sub{font-family:var(--font-display);font-size:1.75rem;font-weight:900;font-style:normal;letter-spacing:-0.03em;color:var(--muted);line-height:1;}
.cp-landing-stats{display:grid;grid-template-columns:repeat(6,1fr);gap:1rem;flex-shrink:0;}
.cp-landing-stat{background:var(--surface);border:1px solid var(--border);border-radius:10px;padding:1rem 1.1rem 0.85rem;display:flex;flex-direction:column;cursor:default;}
.cp-landing-stat-num{font-family:var(--font-display);font-size:2rem;font-weight:900;color:var(--text);line-height:1;margin-bottom:0.25rem;}
.cp-landing-stat-label{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);text-transform:uppercase;letter-spacing:0.08em;margin-bottom:0.3rem;}
.cp-landing-stat-sub{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);line-height:1.4;margin-top:auto;padding-top:0.45rem;border-top:1px solid var(--border);}
.cp-landing-grid{display:grid;grid-template-columns:1fr 1fr;gap:1rem;flex:1;min-height:0;}
.cp-landing-card{background:var(--surface);border:1px solid var(--border);border-radius:10px;overflow:hidden;display:flex;flex-direction:column;}
.cp-landing-card-head{padding:0.65rem 1.1rem;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;flex-shrink:0;}
.cp-landing-card-title{font-family:var(--font-display);font-size:0.95rem;font-weight:800;letter-spacing:-0.01em;color:var(--text);}
.cp-landing-card-count{font-family:var(--font-mono);font-size:0.56rem;color:var(--muted);letter-spacing:0.08em;text-transform:uppercase;padding:0.15rem 0.5rem;background:var(--bg);border:1px solid var(--border);border-radius:20px;}
.cp-landing-rows{flex:1;overflow-y:auto;scrollbar-width:none;}
.cp-landing-row{display:flex;align-items:center;gap:0.75rem;padding:0.55rem 1.1rem;border-bottom:1px solid var(--border);cursor:pointer;transition:background 0.12s;}
.cp-landing-row:last-child{border-bottom:none;}
.cp-landing-row:hover{background:var(--surface2);}
.cp-landing-row-label{flex:1;min-width:0;}
.cp-landing-row-name{font-size:0.82rem;font-weight:500;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.cp-landing-row-sub{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);margin-top:0.15rem;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.cp-landing-row-val{font-family:var(--font-mono);font-size:0.62rem;color:var(--text);font-weight:600;flex-shrink:0;padding:0.15rem 0.5rem;border-radius:4px;border:1px solid var(--border);background:var(--bg);white-space:nowrap;}
.cp-landing-row-val.urgent{color:#e74c3c;background:rgba(192,57,43,0.08);border-color:rgba(192,57,43,0.25);}
.cp-landing-row-val.green{color:#27ae60;background:rgba(46,125,82,0.08);border-color:rgba(46,125,82,0.2);}
.cp-landing-empty{padding:2rem 1.1rem;text-align:center;font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);}
[data-theme="light"] .cp-landing-stat{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .cp-landing-stat-num{color:#1a2744;}
[data-theme="light"] .cp-landing-stat-label{color:#4a5568;}
[data-theme="light"] .cp-landing-stat-sub{color:#6b7280;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .cp-landing-card{background:#ffffff;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .cp-landing-card-head{background:#f0f3f9;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .cp-landing-card-title{color:#1a1e2e;}
[data-theme="light"] .cp-landing-row-val{background:rgba(26,39,68,0.05);border-color:rgba(26,39,68,0.15);color:#1a2744;}
[data-theme="light"] .cp-landing-row{border-color:rgba(26,39,68,0.07);}
[data-theme="light"] .cp-landing-row:hover{background:#edf0f7;}
[data-theme="light"] .cp-landing-row-name{color:#1a1e2e;}
[data-theme="light"] .cp-landing-row-sub{color:#6b7280;}

/* LIGHT MODE */
[data-theme="light"] .cp-sidebar{background:#1a2744;}
[data-theme="light"] .cp-sidebar-head{border-color:rgba(255,255,255,0.1);}
[data-theme="light"] .cp-sidebar-title{color:#e6e4dc;}
[data-theme="light"] .cp-sidebar-title em{color:#c9a84c;}
[data-theme="light"] .cp-add-btn{background:rgba(201,168,76,0.1);border-color:rgba(201,168,76,0.3);color:#c9a84c;}
[data-theme="light"] .cp-search{background:rgba(255,255,255,0.08);border-color:rgba(255,255,255,0.1);color:#e6e4dc;}
[data-theme="light"] .cp-search::placeholder{color:rgba(255,255,255,0.35);}
[data-theme="light"] .cp-client-list{background:#1a2744;}
[data-theme="light"] .cp-client-item:hover{background:rgba(255,255,255,0.06);}
[data-theme="light"] .cp-client-item.active{background:rgba(201,168,76,0.12);border-color:rgba(201,168,76,0.3);}
[data-theme="light"] .cp-client-name{color:#e6e4dc;}
[data-theme="light"] .cp-client-sub{color:rgba(255,255,255,0.45);}
[data-theme="light"] .cp-main{background:#eef1f7;}
[data-theme="light"] .cp-header{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .cp-header::before{background:linear-gradient(90deg,transparent,#1a2744,transparent);}
[data-theme="light"] .cp-big-name{color:#1a1e2e;}
[data-theme="light"] .cp-stat-num{color:#1a2744;}
[data-theme="light"] .cp-stats{border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .cp-stat{border-color:rgba(26,39,68,0.08);}
[data-theme="light"] .cp-tabs{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .cp-tab.active{color:#1a2744;border-color:#1a2744;}
[data-theme="light"] .cp-card{background:#ffffff;border-color:rgba(26,39,68,0.1);box-shadow:0 1px 3px rgba(26,39,68,0.07);}
[data-theme="light"] .cp-card-head{border-color:rgba(26,39,68,0.08);}
[data-theme="light"] .cp-bill-num{color:#1a2744;}
[data-theme="light"] .cp-bill-row:hover{background:#f0f2f8;}
[data-theme="light"] .cp-bill-row{border-color:rgba(26,39,68,0.06);}
[data-theme="light"] .cp-meeting-day{color:#1a2744;}
[data-theme="light"] .cp-meeting-row{border-color:rgba(26,39,68,0.06);}
[data-theme="light"] .cp-act-row{border-color:rgba(26,39,68,0.06);}
[data-theme="light"] .cp-note-entry{background:#f0f2f8;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .cp-note-text{color:#1a1e2e;}
[data-theme="light"] .cp-add-note-area{background:#f0f2f8;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .cp-field value{color:#1a1e2e;}
[data-theme="light"] .cp-report-card{background:linear-gradient(135deg,rgba(26,39,68,0.05) 0%,#ffffff 60%);border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .cp-report-btn{background:#1a2744;color:#f8f9fc;}
[data-theme="light"] .cp-report-btn:hover{background:#2d3f6e;}
[data-theme="light"] .cp-modal{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .cp-modal-head{border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .cp-modal-foot{border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .cp-modal-title{color:#1a1e2e;}
[data-theme="light"] .cp-input,.cp-select,.cp-textarea{background:#f0f2f8;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .btn-save{background:#1a2744;border-color:#1a2744;color:#f8f9fc;}
[data-theme="light"] .cp-no-client-title{color:#6b7280;}

/* Client movement dot */
.cp-moved-dot{width:7px;height:7px;border-radius:50%;background:#27ae60;flex-shrink:0;box-shadow:0 0 0 2px rgba(39,174,96,0.25);}
.cp-moved-badge{font-family:var(--font-mono);font-size:0.5rem;padding:0.1rem 0.35rem;border-radius:4px;background:rgba(46,125,82,0.12);color:#27ae60;border:1px solid rgba(46,125,82,0.2);flex-shrink:0;white-space:nowrap;}
`;

// ── MODAL: Add/Edit Client ──────────────────────────
function ClientModal({ client, onClose, onSave, onDelete }) {
  const [form, setForm] = useState(client || {
    name:"", organization:"", industry:"", contact_name:"", contact_email:"",
    contact_phone:"", contract_start:"", contract_end:"", retainer_amount:"",
    priority:"normal", notes:"",
  });
  const set = (k,v) => setForm(f => ({...f, [k]:v}));
  return (
    <div className="cp-overlay" onClick={onClose}>
      <div className="cp-modal" onClick={e => e.stopPropagation()}>
        <div className="cp-modal-head">
          <div className="cp-modal-title">{client ? "Edit Client" : "Add New Client"}</div>
        </div>
        <div className="cp-modal-body">
          <div className="cp-form-group"><label className="cp-label">Client / Organization Name *</label><input className="cp-input" value={form.name} onChange={e=>set("name",e.target.value)} placeholder="Michigan Auto Dealers Association"/></div>
          <div className="cp-form-row">
            <div className="cp-form-group"><label className="cp-label">Industry</label><input className="cp-input" value={form.industry} onChange={e=>set("industry",e.target.value)} placeholder="Automotive"/></div>
            <div className="cp-form-group"><label className="cp-label">Priority</label>
              <select className="cp-select" value={form.priority} onChange={e=>set("priority",e.target.value)}>
                <option value="high">High</option><option value="normal">Normal</option><option value="low">Low</option>
              </select>
            </div>
          </div>
          <div className="cp-form-row">
            <div className="cp-form-group"><label className="cp-label">Primary Contact Name</label><input className="cp-input" value={form.contact_name} onChange={e=>set("contact_name",e.target.value)} placeholder="Jennifer Walsh"/></div>
            <div className="cp-form-group"><label className="cp-label">Contact Email</label><input className="cp-input" value={form.contact_email} onChange={e=>set("contact_email",e.target.value)} placeholder="jwalsh@mada.org"/></div>
          </div>
          <div className="cp-form-row">
            <div className="cp-form-group"><label className="cp-label">Contact Phone</label><input className="cp-input" value={form.contact_phone} onChange={e=>set("contact_phone",e.target.value)} placeholder="(517) 555-0142"/></div>
            <div className="cp-form-group"><label className="cp-label">Monthly Retainer ($)</label><input className="cp-input" type="number" value={form.retainer_amount} onChange={e=>set("retainer_amount",e.target.value)} placeholder="8500"/></div>
          </div>
          <div className="cp-form-row">
            <div className="cp-form-group"><label className="cp-label">Contract Start</label><input className="cp-input" type="date" value={form.contract_start} onChange={e=>set("contract_start",e.target.value)}/></div>
            <div className="cp-form-group"><label className="cp-label">Contract End</label><input className="cp-input" type="date" value={form.contract_end} onChange={e=>set("contract_end",e.target.value)}/></div>
          </div>
        </div>
        <div className="cp-modal-foot">
          {client && onDelete && (
            <button className="btn-cancel" style={{color:"var(--red)",borderColor:"rgba(192,57,43,0.35)",marginRight:"auto"}} onClick={onDelete}>Delete Client</button>
          )}
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={()=>onSave(form)}>Save Client</button>
        </div>
      </div>
    </div>
  );
}

// ── MODAL: Add/Edit Meeting ─────────────────────────
function MeetingModal({ clientId, meeting, onClose, onSave }) {
  const [form, setForm] = useState(meeting || { title:"", meeting_date:"", location:"", attendees:"", legislators:"", notes:"", follow_up:"", follow_up_due:"", completed:false });
  const set = (k,v) => setForm(f => ({...f, [k]:v}));
  return (
    <div className="cp-overlay" onClick={onClose}>
      <div className="cp-modal" onClick={e => e.stopPropagation()}>
        <div className="cp-modal-head"><div className="cp-modal-title">{meeting ? "Edit Meeting" : "Add Meeting"}</div></div>
        <div className="cp-modal-body">
          <div className="cp-form-group"><label className="cp-label">Meeting Title *</label><input className="cp-input" value={form.title} onChange={e=>set("title",e.target.value)} placeholder="Rep. Martinez — HB 4891 Discussion"/></div>
          <div className="cp-form-row">
            <div className="cp-form-group"><label className="cp-label">Date & Time</label><input className="cp-input" type="datetime-local" value={form.meeting_date||""} onChange={e=>set("meeting_date",e.target.value)}/></div>
            <div className="cp-form-group"><label className="cp-label">Location</label><input className="cp-input" value={form.location||""} onChange={e=>set("location",e.target.value)} placeholder="Capitol Office, Lansing"/></div>
          </div>
          <div className="cp-form-row">
            <div className="cp-form-group"><label className="cp-label">Attendees</label><input className="cp-input" value={form.attendees||""} onChange={e=>set("attendees",e.target.value)} placeholder="Jennifer Walsh, Mason Doerr"/></div>
            <div className="cp-form-group"><label className="cp-label">Legislators Present</label><input className="cp-input" value={form.legislators||""} onChange={e=>set("legislators",e.target.value)} placeholder="Rep. Martinez, Sen. Brinks"/></div>
          </div>
          <div className="cp-form-group"><label className="cp-label">Notes / Summary</label><textarea className="cp-textarea" value={form.notes||""} onChange={e=>set("notes",e.target.value)} placeholder="What was discussed, decisions made, next steps…"/></div>
          <div className="cp-form-row">
            <div className="cp-form-group"><label className="cp-label">Follow-up Action</label><input className="cp-input" value={form.follow_up||""} onChange={e=>set("follow_up",e.target.value)} placeholder="Send position paper by Apr 28"/></div>
            <div className="cp-form-group"><label className="cp-label">Follow-up Due Date</label><input className="cp-input" type="date" value={form.follow_up_due||""} onChange={e=>set("follow_up_due",e.target.value)}/></div>
          </div>
        </div>
        <div className="cp-modal-foot">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={()=>onSave({...form, client_id:clientId})}>Save Meeting</button>
        </div>
      </div>
    </div>
  );
}

// ── MODAL: Add/Edit Activity ────────────────────────
function ActivityModal({ clientId, activity, onClose, onSave }) {
  const [form, setForm] = useState(activity || { type:"meeting", description:"", date:new Date().toISOString().split("T")[0], hours:"" });
  const set = (k,v) => setForm(f => ({...f, [k]:v}));
  return (
    <div className="cp-overlay" onClick={onClose}>
      <div className="cp-modal" onClick={e => e.stopPropagation()}>
        <div className="cp-modal-head"><div className="cp-modal-title">{activity ? "Edit Activity" : "Log Activity"}</div></div>
        <div className="cp-modal-body">
          <div className="cp-form-row">
            <div className="cp-form-group"><label className="cp-label">Activity Type</label>
              <select className="cp-select" value={form.type} onChange={e=>set("type",e.target.value)}>
                <option value="meeting">Meeting</option><option value="call">Phone Call</option>
                <option value="email">Email / Correspondence</option><option value="testimony">Testimony</option>
                <option value="research">Research / Analysis</option><option value="other">Other</option>
              </select>
            </div>
            <div className="cp-form-group"><label className="cp-label">Date</label><input className="cp-input" type="date" value={form.date} onChange={e=>set("date",e.target.value)}/></div>
          </div>
          <div className="cp-form-group"><label className="cp-label">Description *</label><textarea className="cp-textarea" value={form.description} onChange={e=>set("description",e.target.value)} placeholder="Describe the activity…"/></div>
          <div className="cp-form-group"><label className="cp-label">Hours Logged</label><input className="cp-input" type="number" step="0.5" value={form.hours} onChange={e=>set("hours",e.target.value)} placeholder="1.5"/></div>
        </div>
        <div className="cp-modal-foot">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={()=>onSave({...form, client_id:clientId})}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── MODAL: Add/Edit Bill ────────────────────────────
function BillModal({ clientId, bill, onClose, onSave }) {
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedBill, setSelectedBill] = useState(bill || null);
  const [position, setPosition] = useState(bill?.position || "monitor");
  const [nickname, setNickname] = useState(bill?.nickname || "");
  const [notes, setNotes] = useState(bill?.notes || "");
  const isEdit = !!bill;

  useEffect(() => {
    if (isEdit || !search.trim() || search.length < 3) { setResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`${SERVER}/api/db/search?query=${encodeURIComponent(search)}&limit=8`);
        const d = await res.json();
        setResults(d.results || []);
      } catch { setResults([]); }
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [search, isEdit]);

  return (
    <div className="cp-overlay" onClick={onClose}>
      <div className="cp-modal" onClick={e => e.stopPropagation()}>
        <div className="cp-modal-head"><div className="cp-modal-title">{isEdit ? `Edit — ${bill.bill_number}` : "Track a Bill"}</div></div>
        <div className="cp-modal-body">
          {!isEdit && (
            <>
              <div className="cp-form-group">
                <label className="cp-label">Search Bills</label>
                <input className="cp-input" value={search} onChange={e=>{setSearch(e.target.value);setSelectedBill(null);}} placeholder="HB 4482 or keyword…"/>
              </div>
              {searching && <div style={{fontFamily:"var(--font-mono)",fontSize:"0.65rem",color:"var(--muted)"}}>Searching…</div>}
              {results.map(b => (
                <div key={b.bill_id} onClick={()=>setSelectedBill(b)} style={{padding:"0.6rem 0.75rem",borderRadius:8,border:`1px solid ${selectedBill?.bill_id===b.bill_id?"var(--gold-dim)":"var(--border)"}`,background:selectedBill?.bill_id===b.bill_id?"var(--gold-glow)":"var(--surface2)",cursor:"pointer",marginBottom:3}}>
                  <div style={{fontFamily:"var(--font-mono)",fontSize:"0.65rem",color:"var(--gold)",marginBottom:2}}>{b.bill_number}</div>
                  <div style={{fontSize:"0.78rem",color:"var(--text)"}}>{b.title}</div>
                  <div style={{fontFamily:"var(--font-mono)",fontSize:"0.55rem",color:"var(--muted)",marginTop:2}}>{displayStatus(b.status)}</div>
                </div>
              ))}
            </>
          )}
          {(selectedBill || isEdit) && (
            <>
              <div className="cp-form-group">
                <label className="cp-label">Client Position</label>
                <select className="cp-select" value={position} onChange={e=>setPosition(e.target.value)}>
                  <option value="support">Support</option><option value="oppose">Oppose</option>
                  <option value="monitor">Monitor</option><option value="neutral">Neutral</option>
                </select>
              </div>
              <div className="cp-form-group">
                <label className="cp-label">Nickname / Short Label</label>
                <input className="cp-input" value={nickname} onChange={e=>setNickname(e.target.value)} placeholder="e.g. Dealer Franchise Bill"/>
              </div>
              <div className="cp-form-group">
                <label className="cp-label">Notes</label>
                <textarea className="cp-textarea" value={notes} onChange={e=>setNotes(e.target.value)} placeholder="Client's specific concerns, talking points, history…"/>
              </div>
            </>
          )}
        </div>
        <div className="cp-modal-foot">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" disabled={!isEdit && !selectedBill} onClick={()=>{
            if (isEdit) {
              onSave({ ...bill, position, nickname, notes });
            } else if (selectedBill) {
              onSave({ client_id:clientId, bill_id:selectedBill.bill_id, bill_number:selectedBill.bill_number, title:selectedBill.title, position, nickname, notes, status:selectedBill.status_text||selectedBill.status, added_at:new Date().toISOString() });
            }
          }}>{isEdit ? "Save Changes" : "Track Bill"}</button>
        </div>
      </div>
    </div>
  );
}

// ── AVATAR COLORS ───────────────────────────────────
const AVATAR_COLORS = [
  {bg:"rgba(201,168,76,0.15)",border:"rgba(201,168,76,0.3)",text:"#c9a84c"},
  {bg:"rgba(59,111,212,0.15)",border:"rgba(59,111,212,0.3)",text:"#3b6fd4"},
  {bg:"rgba(46,125,82,0.15)",border:"rgba(46,125,82,0.3)",text:"#27ae60"},
  {bg:"rgba(192,57,43,0.12)",border:"rgba(192,57,43,0.3)",text:"#e74c3c"},
  {bg:"rgba(155,89,182,0.12)",border:"rgba(155,89,182,0.3)",text:"#9b59b6"},
  {bg:"rgba(52,152,219,0.12)",border:"rgba(52,152,219,0.3)",text:"#3498db"},
];
const getColor = (id) => AVATAR_COLORS[Math.abs((id||"").charCodeAt(0)||0) % AVATAR_COLORS.length];
const initials = (name) => name?.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase() || "?";

// Round to nearest quarter, strip unnecessary trailing zeros
function fmtHours(h) {
  const n = Math.round((parseFloat(h)||0) * 4) / 4;
  return n % 1 === 0 ? String(n) : n.toFixed(2).replace(/0$/, "");
}

const POSITION_STYLES = {
  support:  {cls:"pos-support",label:"SUPPORT"},
  oppose:   {cls:"pos-oppose", label:"OPPOSE"},
  monitor:  {cls:"pos-monitor",label:"MONITOR"},
  neutral:  {cls:"pos-neutral",label:"NEUTRAL"},
};
const ACT_ICONS = {meeting:"🤝",call:"📞",email:"✉️",testimony:"📄",research:"🔍",other:"📌"};
const ACT_CLS   = {meeting:"act-meeting",call:"act-call",email:"act-email",testimony:"act-testimony",research:"act-research",other:"act-meeting"};

// ── LANDING DASHBOARD ──────────────────────────────────
function LandingDashboard({ clients, allBills, allMeetings, allActivity, onSelectClient, onAddClient }) {
  const now = new Date();
  const weekAgo = new Date(now - 7*24*60*60*1000);
  const weekAhead = new Date(now.getTime() + 7*24*60*60*1000);
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

  // Stats
  const totalClients   = clients.length;
  const totalBills     = allBills.length;
  const upcomingCount  = allMeetings.filter(m => m.meeting_date && new Date(m.meeting_date) >= now).length;
  const weekActivity   = allActivity.filter(a => a.date && new Date(a.date) >= weekAgo).length;
  const monthHours     = allActivity
    .filter(a => a.date && new Date(a.date) >= monthStart)
    .reduce((s,a) => s + (parseFloat(a.hours)||0), 0);
  const totalRetainer  = clients.reduce((s,c) => s + (parseFloat(c.retainer_amount)||0), 0);

  // Follow-ups due in next 7 days
  const followUpsDue = allMeetings
    .filter(m => m.follow_up_due && new Date(m.follow_up_due) >= now && new Date(m.follow_up_due) <= weekAhead && !m.completed)
    .sort((a,b) => new Date(a.follow_up_due) - new Date(b.follow_up_due))
    .slice(0,5)
    .map(m => ({ ...m, clientName: clients.find(c=>c.id===m.client_id)?.name || "Unknown" }));

  // Top clients by hours this month
  const clientHours = clients.map(c => ({
    ...c,
    hours: allActivity.filter(a => a.client_id===c.id && a.date && new Date(a.date) >= monthStart)
      .reduce((s,a) => s + (parseFloat(a.hours)||0), 0)
  })).filter(c => c.hours > 0).sort((a,b) => b.hours - a.hours).slice(0,5);

  // Bills with movement (non-Introduced statuses)
  const movedBills = allBills
    .filter(b => ["In Progress","Passed","PA Signed","In Committee"].includes(displayStatus(b.status)))
    .slice(0,6)
    .map(b => ({ ...b, clientName: clients.find(c=>c.id===b.client_id)?.name || "Unknown" }));

  // Upcoming meetings across all clients
  const nextMeetings = allMeetings
    .filter(m => m.meeting_date && new Date(m.meeting_date) >= now)
    .sort((a,b) => new Date(a.meeting_date) - new Date(b.meeting_date))
    .slice(0,5)
    .map(m => ({ ...m, clientName: clients.find(c=>c.id===m.client_id)?.name || "Unknown" }));

  return (
    <div className="cp-landing">
      <div className="cp-landing-title">
        <span className="cp-landing-title-main">Client Services</span>
        <span className="cp-landing-title-sub">Overview</span>
      </div>

      {/* STAT ROW */}
      <div className="cp-landing-stats">
        <div className="cp-landing-stat">
          <div className="cp-landing-stat-num">{totalClients}</div>
          <div className="cp-landing-stat-label">Active Clients</div>
          <div className="cp-landing-stat-sub">{clients.filter(c=>c.priority==="high").length} high priority</div>
        </div>
        <div className="cp-landing-stat">
          <div className="cp-landing-stat-num">{totalBills}</div>
          <div className="cp-landing-stat-label">Bills Being Monitored</div>
          <div className="cp-landing-stat-sub">{allBills.filter(b=>b.position==="monitor").length} monitor · {allBills.filter(b=>b.position==="support").length} support · {allBills.filter(b=>b.position==="oppose").length} oppose</div>
        </div>
        <div className="cp-landing-stat">
          <div className="cp-landing-stat-num">{upcomingCount}</div>
          <div className="cp-landing-stat-label">Upcoming Meetings</div>
          <div className="cp-landing-stat-sub">{followUpsDue.length} follow-ups due this week</div>
        </div>
        <div className="cp-landing-stat">
          <div className="cp-landing-stat-num">{weekActivity}</div>
          <div className="cp-landing-stat-label">Activities This Week</div>
          <div className="cp-landing-stat-sub">across all clients</div>
        </div>
        <div className="cp-landing-stat">
          <div className="cp-landing-stat-num">{fmtHours(monthHours)}</div>
          <div className="cp-landing-stat-label">Hours Logged This Month</div>
          <div className="cp-landing-stat-sub">{allActivity.filter(a=>a.date&&new Date(a.date)>=weekAgo).reduce((s,a)=>s+(parseFloat(a.hours)||0),0)} hrs this week</div>
        </div>
        <div className="cp-landing-stat">
          <div className="cp-landing-stat-num">{movedBills.length}</div>
          <div className="cp-landing-stat-label">Bills With Movement</div>
          <div className="cp-landing-stat-sub">{allBills.filter(b=>["Passed","PA Signed"].includes(displayStatus(b.status))).length} passed or signed</div>
        </div>
      </div>

      {/* GRID */}
      <div className="cp-landing-grid">

        {/* Follow-ups due */}
        <div className="cp-landing-card">
          <div className="cp-landing-card-head">
            <div className="cp-landing-card-title">Follow-ups Due This Week</div>
            <div className="cp-landing-card-count">{followUpsDue.length} PENDING</div>
          </div>
          {followUpsDue.length === 0
            ? <div className="cp-landing-empty">No follow-ups due in the next 7 days</div>
            : followUpsDue.map(m => {
                const due = new Date(m.follow_up_due);
                const daysLeft = Math.ceil((due - now) / (1000*60*60*24));
                return (
                  <div key={m.id} className="cp-landing-row" onClick={()=>onSelectClient(m.client_id)}>
                    <div className="cp-landing-row-label">
                      <div className="cp-landing-row-name">{m.follow_up}</div>
                      <div className="cp-landing-row-sub">{m.clientName} · {m.title}</div>
                    </div>
                    <div className={`cp-landing-row-val ${daysLeft<=2?"urgent":""}`}>
                      {daysLeft===0?"TODAY":daysLeft===1?"TOMORROW":`${daysLeft}d`}
                    </div>
                  </div>
                );
              })
          }
        </div>

        {/* Upcoming meetings */}
        <div className="cp-landing-card">
          <div className="cp-landing-card-head">
            <div className="cp-landing-card-title">Next Meetings</div>
            <div className="cp-landing-card-count">{nextMeetings.length} UPCOMING</div>
          </div>
          {nextMeetings.length === 0
            ? <div className="cp-landing-empty">No upcoming meetings scheduled</div>
            : nextMeetings.map(m => {
                const d = new Date(m.meeting_date);
                return (
                  <div key={m.id} className="cp-landing-row" onClick={()=>onSelectClient(m.client_id)}>
                    <div className="cp-landing-row-label">
                      <div className="cp-landing-row-name">{m.title}</div>
                      <div className="cp-landing-row-sub">{m.clientName}{m.location?` · ${m.location}`:""}</div>
                    </div>
                    <div className="cp-landing-row-val">
                      {d.toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                    </div>
                  </div>
                );
              })
          }
        </div>

        {/* Bills with movement */}
        <div className="cp-landing-card">
          <div className="cp-landing-card-head">
            <div className="cp-landing-card-title">Bills With Movement</div>
            <div className="cp-landing-card-count">{movedBills.length} ACTIVE</div>
          </div>
          {movedBills.length === 0
            ? <div className="cp-landing-empty">No tracked bills with recent movement</div>
            : movedBills.map(b => {
                const ps = POSITION_STYLES[b.position]||POSITION_STYLES.neutral;
                return (
                  <div key={b.id} className="cp-landing-row" onClick={()=>onSelectClient(b.client_id)}>
                    <div style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--gold)",fontWeight:600,width:52,flexShrink:0}}>{b.bill_number}</div>
                    <div className="cp-landing-row-label">
                      <div className="cp-landing-row-name">{b.nickname||b.title}</div>
                      <div className="cp-landing-row-sub">{b.clientName}</div>
                    </div>
                    <div className="cp-landing-row-val green">{displayStatus(b.status)}</div>
                  </div>
                );
              })
          }
        </div>

        {/* Top clients by hours */}
        <div className="cp-landing-card">
          <div className="cp-landing-card-head">
            <div className="cp-landing-card-title">Top Clients by Hours</div>
            <div className="cp-landing-card-count">THIS MONTH</div>
          </div>
          {clientHours.length === 0
            ? <div className="cp-landing-empty">No hours logged this month yet</div>
            : clientHours.map((c,i) => {
                const col = getColor(c.id);
                return (
                  <div key={c.id} className="cp-landing-row" onClick={()=>onSelectClient(c.id)}>
                    <div className="cp-avatar" style={{background:col.bg,borderColor:col.border,color:col.text,width:28,height:28,fontSize:"0.55rem"}}>{initials(c.name)}</div>
                    <div className="cp-landing-row-label">
                      <div className="cp-landing-row-name">{c.name}</div>
                      <div className="cp-landing-row-sub">{c.industry||"—"}</div>
                    </div>
                    <div className="cp-landing-row-val">{fmtHours(c.hours)}h</div>
                  </div>
                );
              })
          }
        </div>

      </div>
    </div>
  );
}

// ── MAIN COMPONENT ──────────────────────────────────
export default function ClientServicesPage({ onNavigate, session, profile }) {
  const userId = session?.user?.id;
  const authHeaders = () => ({
    "Content-Type": "application/json",
    ...(session?.access_token ? { "Authorization": `Bearer ${session.access_token}` } : {}),
  });
  const { toasts, toast, dismiss } = useToast();
  const [confirmState, setConfirmState] = useState(null);
  const confirm = (message, subtext, onConfirm) => setConfirmState({ message, subtext, onConfirm });

  const [clients,     setClients]     = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [tab,         setTab]         = useState("overview");
  const [bills,       setBills]       = useState([]);
  const [meetings,    setMeetings]    = useState([]);
  const [activity,    setActivity]    = useState([]);
  const [notes,       setNotes]       = useState([]);
  const [searchQ,     setSearchQ]     = useState("");
  const [generating,  setGenerating]  = useState(false);
  const [genStatus,   setGenStatus]   = useState("");
  const [newNoteText, setNewNoteText] = useState("");
  const [editNoteId,  setEditNoteId]  = useState(null);
  const [editNoteText,setEditNoteText]= useState("");

  // Modals
  const [showAddClient,   setShowAddClient]   = useState(false);
  const [editClient,      setEditClient]      = useState(null);
  const [showAddMeeting,  setShowAddMeeting]  = useState(false);
  const [editMeeting,     setEditMeeting]     = useState(null);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [editActivity,    setEditActivity]    = useState(null);
  const [showAddBill,     setShowAddBill]     = useState(false);
  const [editBill,        setEditBill]        = useState(null);
  const [selectedCpBill,  setSelectedCpBill]  = useState(null);
  const [selectedCpMtg,   setSelectedCpMtg]   = useState(null);
  const [billSearch,      setBillSearch]      = useState("");
  const [reportFrom,      setReportFrom]      = useState("");
  const [reportTo,        setReportTo]        = useState("");
  const [movedClientIds,  setMovedClientIds]  = useState(new Set());

  const client = clients.find(c => c.id === selected);

  // ── Aggregate data for landing dashboard ──
  const [allBills,     setAllBills]     = useState([]);
  const [allMeetings,  setAllMeetings]  = useState([]);
  const [allActivity,  setAllActivity]  = useState([]);

  // ── Load clients ──
  useEffect(() => {
    if (!userId) return;
    supabase.from("clients").select("*").eq("user_id", userId).order("name")
      .then(({data}) => {
        if (data) {
          setClients(data);
          // Auto-open client if navigated from dashboard follow-up
          const openId = sessionStorage.getItem("wc_open_client");
          if (openId) {
            sessionStorage.removeItem("wc_open_client");
            const match = data.find(c => c.id === openId);
            if (match) setSelected(match.id);
          }
        }
      });
  }, [userId]);

  // ── Load aggregate data for landing dashboard ──
  useEffect(() => {
    if (!userId || clients.length === 0) return;
    const ids = clients.map(c => `'${c.id}'`).join(",");
    supabase.from("client_bills").select("*").in("client_id", clients.map(c=>c.id))
      .then(({data}) => setAllBills(data||[]));
    supabase.from("client_meetings").select("*").in("client_id", clients.map(c=>c.id))
      .then(({data}) => setAllMeetings(data||[]));
    supabase.from("client_activity").select("*").in("client_id", clients.map(c=>c.id))
      .then(({data}) => setAllActivity(data||[]));
  }, [clients, userId]);

  // ── Reset per-client UI state when switching ──
  useEffect(() => {
    setBillSearch("");
    setReportFrom("");
    setReportTo("");
  }, [selected]);

  // ── Compute which clients have bill movement since last_opened_at ──
  useEffect(() => {
    if (clients.length === 0 || allBills.length === 0) return;
    const moved = new Set();
    clients.forEach(c => {
      if (!c.last_opened_at) return;
      const lastOpened = new Date(c.last_opened_at);
      const hasMoved = allBills.some(b =>
        b.client_id === c.id &&
        b.last_action_date &&
        new Date(b.last_action_date) > lastOpened &&
        ["In Progress","Passed","PA Signed","In Committee"].includes(
          // use same displayStatus logic inline
          ({Introduced:"Introduced",Engrossed:"In Progress",Enrolled:"PA Signed",Chaptered:"PA Signed",Passed:"Passed",Failed:"Failed",Vetoed:"Vetoed","Report Pass":"In Committee","Report Fail":"In Committee",Refer:"In Committee",Override:"Passed",Draft:"Draft"})[b.status] || b.status
        )
      );
      if (hasMoved) moved.add(c.id);
    });
    setMovedClientIds(moved);
  }, [clients, allBills]);

  // ── Load client data when selected ──
  useEffect(() => {
    if (!selected) return;
    supabase.from("client_bills").select("*").eq("client_id", selected).order("added_at", {ascending:false}).then(({data})=>setBills(data||[]));
    supabase.from("client_meetings").select("*").eq("client_id", selected).order("meeting_date", {ascending:false}).then(({data})=>setMeetings(data||[]));
    supabase.from("client_activity").select("*").eq("client_id", selected).order("date", {ascending:false}).then(({data})=>setActivity(data||[]));
    supabase.from("client_notes").select("*").eq("client_id", selected).order("created_at", {ascending:false}).then(({data})=>setNotes(data||[]));
  }, [selected]);

  // ── CRUD: Clients ──
  const saveClient = async (form) => {
    if (!form.name.trim()) return;
    const cleaned = {
      ...form,
      contract_start:  form.contract_start  || null,
      contract_end:    form.contract_end    || null,
      retainer_amount: parseFloat(form.retainer_amount) || null,
    };
    if (editClient) {
      const {data} = await supabase.from("clients").update({...cleaned, updated_at:new Date().toISOString()}).eq("id", editClient.id).select().single();
      if (data) setClients(c => c.map(x => x.id===data.id ? data : x));
    } else {
      const {data} = await supabase.from("clients").insert({...cleaned, user_id:userId, created_at:new Date().toISOString(), updated_at:new Date().toISOString()}).select().single();
      if (data) { setClients(c => [...c, data]); setSelected(data.id); }
    }
    toast(editClient ? "Client updated ✓" : "Client added ✓");
    setShowAddClient(false); setEditClient(null);
  };

  const deleteClient = (id) => {
    confirm(
      `Delete ${clients.find(c=>c.id===id)?.name || 'this client'}?`,
      "All meetings, bills, activity, and notes for this client will be permanently deleted.",
      async () => {
        await supabase.from("clients").delete().eq("id", id);
        setClients(c => c.filter(x => x.id !== id));
        if (selected === id) setSelected(null);
        toast("Client deleted", "info");
        setConfirmState(null);
      }
    );
  };

  // ── CRUD: Meetings ──
  const saveMeeting = async (form) => {
    const payload = {
      ...form,
      meeting_date:  form.meeting_date  || null,
      follow_up_due: form.follow_up_due || null,
    };
    if (editMeeting) {
      const {data, error} = await supabase.from("client_meetings").update(payload).eq("id", editMeeting.id).select().single();
      if (data) setMeetings(m => m.map(x => x.id===data.id ? data : x));
      if (error) console.error("updateMeeting error:", error);
    } else {
      const {data, error} = await supabase.from("client_meetings").insert({...payload, created_at:new Date().toISOString()}).select().single();
      if (data) setMeetings(m => [data, ...m]);
      if (error) console.error("saveMeeting error:", error);
    }
    toast(editMeeting ? "Meeting updated ✓" : "Meeting saved ✓");
    setShowAddMeeting(false); setEditMeeting(null);
  };

  const deleteMeeting = (id) => {
    confirm("Delete this meeting?", "This action cannot be undone.", async () => {
      await supabase.from("client_meetings").delete().eq("id", id);
      setMeetings(m => m.filter(x => x.id !== id));
      toast("Meeting deleted", "info");
      setConfirmState(null);
    });
  };

  // ── CRUD: Activity ──
  const saveActivity = async (form) => {
    const payload = { ...form, hours: parseFloat(form.hours) || 0 };
    if (editActivity) {
      const {data, error} = await supabase.from("client_activity").update(payload).eq("id", editActivity.id).select().single();
      if (data) setActivity(a => a.map(x => x.id===data.id ? data : x));
      if (error) console.error("updateActivity error:", error);
    } else {
      const {data, error} = await supabase.from("client_activity").insert({...payload, created_at:new Date().toISOString()}).select().single();
      if (data) setActivity(a => [data, ...a]);
      if (error) console.error("saveActivity error:", error);
    }
    toast(editActivity ? "Activity updated ✓" : "Activity logged ✓");
    setShowAddActivity(false); setEditActivity(null);
  };

  const deleteActivity = (id) => {
    confirm("Delete this activity?", "This action cannot be undone.", async () => {
      await supabase.from("client_activity").delete().eq("id", id);
      setActivity(a => a.filter(x => x.id !== id));
      toast("Activity deleted", "info");
      setConfirmState(null);
    });
  };

  // ── CRUD: Bills ──
  const saveBill = async (form) => {
    if (editBill) {
      const {data} = await supabase.from("client_bills").update({position:form.position, nickname:form.nickname, notes:form.notes}).eq("id", editBill.id).select().single();
      if (data) setBills(b => b.map(x => x.id===data.id ? data : x));
    } else {
      const {data} = await supabase.from("client_bills").insert(form).select().single();
      if (data) setBills(b => [data, ...b]);
    }
    toast(editBill ? "Bill updated ✓" : "Bill tracked ✓");
    setShowAddBill(false); setEditBill(null);
  };

  const deleteBill = async (id) => {
    if (!window.confirm("Remove this bill from tracking?")) return;
    await supabase.from("client_bills").delete().eq("id", id);
    setBills(b => b.filter(x => x.id !== id));
  };

  // ── CRUD: Notes ──
  const addNote = async () => {
    if (!newNoteText.trim()) return;
    const {data} = await supabase.from("client_notes").insert({
      client_id: selected,
      text: newNoteText.trim(),
      created_at: new Date().toISOString(),
    }).select().single();
    if (data) { setNotes(n => [data, ...n]); toast("Note added ✓"); }
    setNewNoteText("");
  };

  const saveNoteEdit = async (id) => {
    if (!editNoteText.trim()) return;
    const {data} = await supabase.from("client_notes").update({text: editNoteText.trim()}).eq("id", id).select().single();
    if (data) setNotes(n => n.map(x => x.id===id ? data : x));
    toast("Note saved ✓");
    setEditNoteId(null); setEditNoteText("");
  };

  const deleteNote = async (id) => {
    if (!window.confirm("Delete this note?")) return;
    await supabase.from("client_notes").delete().eq("id", id);
    setNotes(n => n.filter(x => x.id !== id));
  };

  // ── Generate Report ──
  const generateReport = async () => {
    if (!client) return;
    setGenerating(true);
    setGenStatus("Gathering legislative data…");
    // Open window synchronously before any async work so browser doesn't block it as a popup
    const win = window.open("", "_blank");
    if (!win) { alert("Please allow popups for this site to export reports."); setGenerating(false); return; }
    win.document.write("<html><body style='font-family:sans-serif;padding:2rem;color:#333'>Generating your report, please wait…</body></html>");
    try {
      const billsSummary = bills.map(b => `${b.bill_number}${b.nickname?" ("+b.nickname+")":""} (${b.position?.toUpperCase()}): ${b.title} — ${displayStatus(b.status)}`).join("\n");
      const meetingsSummary = meetings.slice(0,10).map(m => `${m.meeting_date ? new Date(m.meeting_date).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : "Date TBD"}: ${m.title}${m.notes ? " — " + m.notes.slice(0,100) : ""}`).join("\n");
      const activitySummary = activity.slice(0,15).map(a => `${a.date}: [${a.type}] ${a.description} (${a.hours||0}hrs)`).join("\n");
      const totalHours = activity.reduce((s,a) => s + (parseFloat(a.hours)||0), 0);
      setGenStatus("Generating executive summary…");
      const prompt = `You are a professional government affairs lobbyist preparing a formal monthly client activity report.\n\nClient: ${client.name}\nIndustry: ${client.industry || "N/A"}\nReporting Period: ${new Date().toLocaleDateString("en-US",{month:"long",year:"numeric"})}\nPrimary Contact: ${client.contact_name || "N/A"}\nTotal Hours Logged: ${fmtHours(totalHours)}\n\nTRACKED LEGISLATION:\n${billsSummary || "No bills tracked."}\n\nMEETINGS THIS PERIOD:\n${meetingsSummary || "No meetings logged."}\n\nACTIVITY LOG:\n${activitySummary || "No activity logged."}\n\nWrite a professional 3-paragraph executive summary for this client report. Paragraph 1: Overview of legislative activity and key focus areas. Paragraph 2: Specific wins, progress, and notable meetings. Paragraph 3: Upcoming priorities and strategic recommendations. Keep it factual, professional, and focused on outcomes. Do not use bullet points.`;
      const res = await fetch(`${SERVER}/api/chat`, {
        method:"POST",
        headers:authHeaders(),
        body: JSON.stringify({ model:"claude-sonnet-4-20250514", max_tokens:1000, messages:[{role:"user",content:prompt}] })
      });
      const d = await res.json();
      const summary = d.content?.[0]?.text || "Unable to generate summary.";
      setGenStatus("Building PDF…");
      const reportHTML = buildReportHTML(client, bills, meetings, activity, summary, totalHours);
      win.document.open();
      win.document.write(reportHTML);
      win.document.close();
      setTimeout(() => win.print(), 800);
    } catch(e) { console.error(e); }
    setGenerating(false);
  };

  function buildReportHTML(c, bills, meetings, activity, summary, totalHours) {
    const month = new Date().toLocaleDateString("en-US",{month:"long",year:"numeric"});
    return `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>WhipCount Report — ${c.name} — ${month}</title><style>@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,900;1,700&family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500&display=swap');*{box-sizing:border-box;margin:0;padding:0;}body{font-family:'IBM Plex Sans',sans-serif;background:#fff;color:#1a1e2e;font-size:9pt;line-height:1.5;}@media print{body{-webkit-print-color-adjust:exact;print-color-adjust:exact;}@page{margin:0.65in;}}.header{background:#1a2744;color:#e6e4dc;padding:18px 24px;}.header-top{display:flex;align-items:center;justify-content:space-between;}.wordmark{font-family:'Playfair Display',serif;font-size:20pt;font-weight:900;color:#c9a84c;}.wordmark em{font-style:italic;}.header-tag{font-family:'IBM Plex Mono',monospace;font-size:6.5pt;color:rgba(255,255,255,0.5);letter-spacing:1.5px;text-align:right;}.gold-line{height:2px;background:linear-gradient(90deg,transparent,#c9a84c,transparent);margin:0 0 20px;}.report-type{font-family:'IBM Plex Mono',monospace;font-size:7pt;color:#6b7280;letter-spacing:2px;text-transform:uppercase;margin-bottom:4px;}.client-name{font-family:'Playfair Display',serif;font-size:22pt;font-weight:900;color:#1a2744;margin-bottom:4px;}.meta-row{display:flex;justify-content:space-between;font-family:'IBM Plex Mono',monospace;font-size:7.5pt;color:#6b7280;margin-bottom:18px;}.stats-row{display:flex;gap:1px;background:#e2e8f0;border:1px solid #e2e8f0;border-radius:6px;overflow:hidden;margin-bottom:22px;}.stat{flex:1;background:#f8f9fc;padding:10px;text-align:center;}.stat-num{font-family:'Playfair Display',serif;font-size:18pt;font-weight:900;color:#1a2744;}.stat-label{font-family:'IBM Plex Mono',monospace;font-size:6pt;color:#6b7280;letter-spacing:1px;text-transform:uppercase;}.section-label{font-family:'IBM Plex Mono',monospace;font-size:7pt;color:#1a2744;letter-spacing:1.5px;text-transform:uppercase;font-weight:600;margin-bottom:6px;margin-top:20px;}.section-line{height:1px;background:#e2e8f0;margin-bottom:12px;}.summary-text{font-size:9pt;line-height:1.75;color:#2d3748;margin-bottom:18px;}table{width:100%;border-collapse:collapse;margin-bottom:18px;font-size:8.5pt;}th{background:#1a2744;color:#e6e4dc;font-family:'IBM Plex Mono',monospace;font-size:6.5pt;font-weight:600;padding:7px 8px;text-align:left;letter-spacing:0.5px;}td{padding:7px 8px;border-bottom:1px solid #f0f2f8;vertical-align:top;}tr:nth-child(even) td{background:#f8f9fc;}.bill-num{font-family:'IBM Plex Mono',monospace;font-size:7.5pt;color:#1a2744;font-weight:600;}.pos-support{color:#27ae60;font-family:'IBM Plex Mono',monospace;font-size:7pt;font-weight:600;}.pos-oppose{color:#e74c3c;font-family:'IBM Plex Mono',monospace;font-size:7pt;font-weight:600;}.pos-monitor{color:#a07830;font-family:'IBM Plex Mono',monospace;font-size:7pt;font-weight:600;}.footer{border-top:1px solid #e2e8f0;padding-top:10px;display:flex;justify-content:space-between;font-family:'IBM Plex Mono',monospace;font-size:6.5pt;color:#6b7280;margin-top:24px;}.footer-brand{font-family:'Playfair Display',serif;font-style:italic;color:#1a2744;font-size:9pt;}</style></head><body><div class="header"><div class="header-top"><div class="wordmark">Whip<em>Count</em>™</div><div class="header-tag">MICHIGAN LEGISLATIVE INTELLIGENCE<br>CLIENT ACTIVITY REPORT</div></div></div><div class="gold-line"></div><div class="report-type">Client Activity Report</div><div class="client-name">${c.name}</div><div class="meta-row"><span>Reporting Period: ${month}</span><span>Prepared by: ${profile?.full_name || "WhipCount User"} · ${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</span></div><div class="stats-row"><div class="stat"><div class="stat-num">${bills.length}</div><div class="stat-label">Bills Tracked</div></div><div class="stat"><div class="stat-num">${meetings.length}</div><div class="stat-label">Meetings</div></div><div class="stat"><div class="stat-num">${fmtHours(totalHours)}</div><div class="stat-label">Hours Logged</div></div>${c.retainer_amount ? `<div class="stat"><div class="stat-num">$${Number(c.retainer_amount).toLocaleString()}</div><div class="stat-label">Monthly Retainer</div></div>` : ""}</div><div class="section-label">Executive Summary</div><div class="section-line"></div><div class="summary-text">${summary.replace(/\n\n/g,"</p><p style='margin-bottom:10px;'>").replace(/\n/g," ")}</div>${bills.length > 0 ? `<div class="section-label">Tracked Legislation</div><div class="section-line"></div><table><tr><th>BILL</th><th>TITLE</th><th>STATUS</th><th>POSITION</th></tr>${bills.map(b=>`<tr><td><span class="bill-num">${b.bill_number}</span>${b.nickname?`<br><span style="font-size:6.5pt;color:#6b7280;font-style:italic;">${b.nickname}</span>`:""}</td><td>${b.title}${b.notes?`<br><span style="font-size:7.5pt;color:#6b7280;font-style:italic;">${b.notes.slice(0,80)}</span>`:""}</td><td style="font-family:'IBM Plex Mono',monospace;font-size:7.5pt;">${displayStatus(b.status)}</td><td><span class="pos-${b.position}">${(b.position||"MONITOR").toUpperCase()}</span></td></tr>`).join("")}</table>` : ""}${meetings.length > 0 ? `<div class="section-label">Meetings & Engagements</div><div class="section-line"></div><table><tr><th>DATE</th><th>MEETING</th><th>LOCATION</th><th>NOTES</th></tr>${meetings.map(m=>`<tr><td style="font-family:'IBM Plex Mono',monospace;font-size:7.5pt;white-space:nowrap;">${m.meeting_date?new Date(m.meeting_date).toLocaleDateString("en-US",{month:"short",day:"numeric"}):""}</td><td style="font-weight:500;">${m.title}</td><td style="font-family:'IBM Plex Mono',monospace;font-size:7.5pt;">${m.location||""}</td><td>${(m.notes||"").slice(0,120)}${(m.notes||"").length>120?"…":""}</td></tr>`).join("")}</table>` : ""}${activity.length > 0 ? `<div class="section-label">Activity & Hours Log</div><div class="section-line"></div><table><tr><th>DATE</th><th>TYPE</th><th>DESCRIPTION</th><th style="text-align:right;">HRS</th></tr>${activity.map(a=>`<tr><td style="font-family:'IBM Plex Mono',monospace;font-size:7.5pt;white-space:nowrap;">${a.date?new Date(a.date).toLocaleDateString("en-US",{month:"short",day:"numeric"}):""}</td><td style="font-family:'IBM Plex Mono',monospace;font-size:7.5pt;text-transform:capitalize;">${a.type||""}</td><td>${a.description||""}</td><td style="font-family:'IBM Plex Mono',monospace;font-size:7.5pt;text-align:right;">${a.hours||""}</td></tr>`).join("")}<tr style="background:#eef1f7 !important;"><td colspan="3" style="font-family:'IBM Plex Mono',monospace;font-size:7.5pt;font-weight:600;color:#1a2744;">TOTAL HOURS</td><td style="font-family:'IBM Plex Mono',monospace;font-size:8pt;font-weight:700;color:#1a2744;text-align:right;">${fmtHours(totalHours)}</td></tr></table>` : ""}<div class="footer"><div><span class="footer-brand">WhipCount™</span> — Michigan Legislative Intelligence</div><div>Confidential — Prepared for ${c.name} · ${new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</div></div></body></html>`;
  }

  const filteredClients = clients.filter(c =>
    !searchQ || c.name.toLowerCase().includes(searchQ.toLowerCase()) || (c.industry||"").toLowerCase().includes(searchQ.toLowerCase())
  );

  const totalHours = activity.reduce((s,a) => s + (parseFloat(a.hours)||0), 0);
  const upcomingMeetings = meetings.filter(m => m.meeting_date && new Date(m.meeting_date) >= new Date());
  const pastMeetings     = meetings.filter(m => !m.meeting_date || new Date(m.meeting_date) < new Date());

  const AvatarImg = ({ c, size="sm" }) => {
    const col = getColor(c.id);
    const cls = size==="lg" ? "cp-big-avatar" : "cp-avatar";
    return <div className={cls} style={{background:col.bg,borderColor:col.border,color:col.text}}>{initials(c.name)}</div>;
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="page">
        {/* MASTHEAD */}
        <div className="legisim-masthead">
          <div className="masthead-left">
            <div className="masthead-wordmark" onClick={()=>onNavigate("dashboard")}>Whip<em>Count</em><sup>TM</sup></div>
            <button className="mast-back" onClick={()=>onNavigate("dashboard")}>← Dashboard</button>
          </div>
          <div className="masthead-right">
            <button className="mast-btn-sm" onClick={()=>onNavigate("account")}>Account</button>
            <button className="mast-btn-sm signout" onClick={()=>handleSignOut(supabase, onNavigate)}>Sign Out</button>
          </div>
        </div>

        <div className="cp-body">
          {/* SIDEBAR */}
          <div className="cp-sidebar">
            <div className="cp-sidebar-head">
              <div className="cp-sidebar-title">Client <em>Services</em></div>
              <button className="cp-add-btn" onClick={()=>setShowAddClient(true)}>+ Add New Client</button>
            </div>
            <input className="cp-search" placeholder="Search clients…" value={searchQ} onChange={e=>setSearchQ(e.target.value)}/>
            <div className="cp-client-list">
              {filteredClients.length === 0 && (
                <div className="cp-empty" style={{padding:"1.5rem"}}>
                  <div className="cp-empty-icon">👥</div>
                  <div className="cp-empty-text">{clients.length === 0 ? "No clients yet.\nClick + to add your first." : "No matches found."}</div>
                </div>
              )}
              {filteredClients.map(c => (
                <div key={c.id} className={`cp-client-item${selected===c.id?" active":""}`} onClick={async ()=>{
                  setSelected(c.id); setTab("overview");
                  const now = new Date().toISOString();
                  await supabase.from("clients").update({ last_opened_at: now }).eq("id", c.id);
                  setClients(prev => prev.map(x => x.id===c.id ? {...x, last_opened_at: now} : x));
                  setMovedClientIds(prev => { const n = new Set(prev); n.delete(c.id); return n; });
                }}>
                  <AvatarImg c={c} size="sm"/>
                  <div className="cp-client-info">
                    <div className="cp-client-name">{c.name}</div>
                    <div className="cp-client-sub">{c.industry||"—"}</div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:"0.35rem"}}>
                    {movedClientIds.has(c.id) && <div className="cp-moved-dot" title="Bills moved since last opened"/>}
                    <div className={`cp-priority-dot dot-${c.priority||"normal"}`}/>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* MAIN AREA */}
          <div className="cp-main">
            {!client ? (
              <LandingDashboard
                clients={clients}
                allBills={allBills}
                allMeetings={allMeetings}
                allActivity={allActivity}
                onSelectClient={async (id)=>{
                  setSelected(id); setTab("overview");
                  const now = new Date().toISOString();
                  await supabase.from("clients").update({ last_opened_at: now }).eq("id", id);
                  setClients(prev => prev.map(x => x.id===id ? {...x, last_opened_at: now} : x));
                  setMovedClientIds(prev => { const n = new Set(prev); n.delete(id); return n; });
                }}
                onAddClient={()=>setShowAddClient(true)}
              />
            ) : (
              <>
                {/* CLIENT HEADER */}
                <div className="cp-header">
                  <button className="cp-back-btn" onClick={()=>setSelected(null)}>
                    <span className="cp-back-btn-arrow">←</span>
                    <span className="cp-back-btn-text">← Client Overview</span>
                  </button>
                  <div className="cp-header-top">
                    <div className="cp-header-info">
                      <div className="cp-big-name">{client.name}</div>
                      <div className="cp-big-sub">
                        {client.contact_name && `${client.contact_name}`}
                        {client.contact_email && ` · ${client.contact_email}`}
                        {client.contact_phone && ` · ${client.contact_phone}`}
                      </div>
                      <div className="cp-badges">
                        {client.priority==="high" && <span className="cp-badge badge-high">● HIGH PRIORITY</span>}
                        {client.industry && <span className="cp-badge badge-ind">{client.industry.toUpperCase()}</span>}
                        {client.contract_end && new Date(client.contract_end) > new Date() && <span className="cp-badge badge-active">● CONTRACT ACTIVE</span>}
                        {client.retainer_amount && <span className="cp-badge badge-neutral">RETAINER: ${Number(client.retainer_amount).toLocaleString()}/mo</span>}
                      </div>
                    </div>
                    <div className="cp-header-actions">
                      <button className="cp-action-btn btn-gold" onClick={generateReport} disabled={generating}>
                        {generating ? "Generating…" : "⬇ Generate Report"}
                      </button>
                      <button className="cp-action-btn btn-outline" onClick={()=>setEditClient(client)}>Edit</button>
                      <button className="cp-action-btn btn-outline" onClick={()=>setShowAddActivity(true)}>+ Log Activity</button>
                    </div>
                  </div>
                  <div className="cp-stats">
                    <div className="cp-stat"><div className="cp-stat-num">{bills.length}</div><div className="cp-stat-label">Bills Tracked</div></div>
                    <div className="cp-stat"><div className="cp-stat-num">{upcomingMeetings.length}</div><div className="cp-stat-label">Upcoming Meetings</div></div>
                    <div className="cp-stat"><div className="cp-stat-num">{fmtHours(totalHours)}</div><div className="cp-stat-label">Hours Logged</div></div>
                    <div className="cp-stat"><div className="cp-stat-num">{activity.length}</div><div className="cp-stat-label">Activities</div></div>
                  </div>
                </div>

                {/* TABS */}
                <div className="cp-tabs">
                  {["overview","bills","meetings","activity","notes"].map(t => (
                    <div key={t} className={`cp-tab${tab===t?" active":""}`} onClick={()=>setTab(t)}>
                      {t==="bills"?`BILLS (${bills.length})`:t==="meetings"?`MEETINGS (${meetings.length})`:t==="activity"?`ACTIVITY (${activity.length})`:t==="notes"?`NOTES (${notes.length})`:t.toUpperCase()}
                    </div>
                  ))}
                </div>

                {/* TAB CONTENT */}
                <div className="cp-content">

                  {/* OVERVIEW */}
                  {tab==="overview" && (
                    <div className="cp-grid">
                      <div className="cp-card">
                        <div className="cp-card-head">
                          <div className="cp-card-title">Tracked Bills</div>
                          <div className="cp-card-action" onClick={()=>{setTab("bills");setShowAddBill(true);}}>+ Add Bill</div>
                        </div>
                        {bills.length===0 ? <div className="cp-empty"><div className="cp-empty-icon">📋</div><div className="cp-empty-text">No bills tracked yet</div></div> :
                          bills.slice(0,5).map(b => {
                            const ps = POSITION_STYLES[b.position]||POSITION_STYLES.neutral;
                            const moved = ["In Progress","Passed","PA Signed"].includes(displayStatus(b.status));
                            return (
                              <div key={b.id} className="cp-bill-row" onClick={() => setSelectedCpBill(b)}>
                                <div className="cp-bill-num">{b.bill_number}</div>
                                <div className="cp-bill-info">
                                  <div className="cp-bill-title">{b.nickname || b.title}</div>
                                  <div className="cp-bill-meta">{displayStatus(b.status)}</div>
                                </div>
                                <span className={`pos-tag ${ps.cls}`}>{ps.label}</span>
                                {moved && <span className="moved-tag">▲ ACTIVE</span>}
                              </div>
                            );
                          })
                        }
                        {bills.length>5 && <div style={{padding:"0.5rem 1.1rem",fontFamily:"var(--font-mono)",fontSize:"0.58rem",color:"var(--gold)",cursor:"pointer"}} onClick={()=>setTab("bills")}>View all {bills.length} bills →</div>}
                      </div>

                      <div className="cp-card">
                        <div className="cp-card-head">
                          <div className="cp-card-title">Upcoming Meetings</div>
                          <div className="cp-card-action" onClick={()=>setShowAddMeeting(true)}>+ Add</div>
                        </div>
                        {upcomingMeetings.length===0 ? <div className="cp-empty"><div className="cp-empty-icon">📅</div><div className="cp-empty-text">No upcoming meetings</div></div> :
                          upcomingMeetings.slice(0,4).map(m => {
                            const d = m.meeting_date ? new Date(m.meeting_date) : null;
                            return (
                              <div key={m.id} className="cp-meeting-row" onClick={() => setSelectedCpMtg(m)}>
                                <div className="cp-meeting-date">
                                  <div className="cp-meeting-month">{d?.toLocaleDateString("en-US",{month:"short"})||"TBD"}</div>
                                  <div className="cp-meeting-day">{d?.getDate()||"?"}</div>
                                </div>
                                <div className="cp-meeting-info">
                                  <div className="cp-meeting-title">{m.title}</div>
                                  <div className="cp-meeting-sub">{m.location}{m.attendees?` · ${m.attendees}`:""}</div>
                                  {m.follow_up && <div className="cp-meeting-followup">↳ {m.follow_up}</div>}
                                </div>
                              </div>
                            );
                          })
                        }
                      </div>

                      <div className="cp-card">
                        <div className="cp-card-head">
                          <div className="cp-card-title">Recent Activity</div>
                          <div className="cp-card-action" onClick={()=>setShowAddActivity(true)}>+ Log</div>
                        </div>
                        {activity.length===0 ? <div className="cp-empty"><div className="cp-empty-icon">📝</div><div className="cp-empty-text">No activity logged yet</div></div> :
                          activity.slice(0,5).map(a => (
                            <div key={a.id} className="cp-act-row">
                              <div className={`cp-act-icon ${ACT_CLS[a.type]||"act-meeting"}`}>{ACT_ICONS[a.type]||"📌"}</div>
                              <div className="cp-act-info">
                                <div className="cp-act-desc">{a.description}</div>
                                <div className="cp-act-meta">{a.date?new Date(a.date).toLocaleDateString("en-US",{month:"short",day:"numeric"}):""} · {a.type}</div>
                              </div>
                              {a.hours ? <div className="cp-act-hrs">{fmtHours(a.hours)}h</div> : null}
                              <button className="cp-act-del" title="Delete" onClick={e=>{e.stopPropagation();deleteActivity(a.id);}}>✕</button>
                            </div>
                          ))
                        }
                      </div>

                      <div className="cp-card">
                        <div className="cp-card-head">
                          <div className="cp-card-title">Client Information</div>
                          <div className="cp-card-action" onClick={()=>setEditClient(client)}>Edit</div>
                        </div>
                        <div className="cp-contact-grid">
                          <div className="cp-field"><label>Organization</label><value>{client.name}</value></div>
                          <div className="cp-field"><label>Industry</label><value>{client.industry||"—"}</value></div>
                          <div className="cp-field"><label>Primary Contact</label><value>{client.contact_name||"—"}</value></div>
                          <div className="cp-field"><label>Email</label><value>{client.contact_email ? <a href={`mailto:${client.contact_email}`} style={{color:"var(--gold)",textDecoration:"none"}}>{client.contact_email}</a> : "—"}</value></div>
                          <div className="cp-field"><label>Phone</label><value>{client.contact_phone ? <a href={`tel:${client.contact_phone}`} style={{color:"var(--gold)",textDecoration:"none"}}>{client.contact_phone}</a> : "—"}</value></div>
                          <div className="cp-field"><label>Monthly Retainer</label><value>{client.retainer_amount?`$${Number(client.retainer_amount).toLocaleString()}`:"—"}</value></div>
                          <div className="cp-field"><label>Contract Start</label><value>{client.contract_start||"—"}</value></div>
                          <div className="cp-field"><label>Contract End</label><value>{client.contract_end||"—"}</value></div>
                        </div>
                      </div>

                      <div className="cp-report-card cp-full">
                        <div className="cp-report-icon">📋</div>
                        <div className="cp-report-info">
                          <div className="cp-report-title">Generate Client Report — {client.name}</div>
                          <div className="cp-report-desc">
                            AI-powered report · {bills.length} tracked bills · {meetings.length} meetings · {fmtHours(totalHours)} hours logged<br/>
                            Includes bill status, meeting recap, activity log, and AI executive summary. Opens print dialog for PDF export.
                          </div>
                        </div>
                        <button className="cp-report-btn" onClick={generateReport} disabled={generating}>
                          {generating ? genStatus : "⬇ Generate Report"}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* BILLS TAB */}
                  {tab==="bills" && (() => {
                    const filteredBills = bills.filter(b => {
                      if (!billSearch.trim()) return true;
                      const q = billSearch.toLowerCase();
                      return (b.bill_number||"").toLowerCase().includes(q) ||
                             (b.title||"").toLowerCase().includes(q) ||
                             (b.nickname||"").toLowerCase().includes(q) ||
                             (b.notes||"").toLowerCase().includes(q) ||
                             (b.position||"").toLowerCase().includes(q);
                    });
                    return (
                    <div>
                      <div style={{display:"flex",alignItems:"center",gap:"0.75rem",marginBottom:"0.85rem"}}>
                        <input
                          style={{flex:1,background:"var(--surface2)",border:"1px solid var(--border)",borderRadius:"7px",padding:"0.45rem 0.75rem",fontFamily:"var(--font-mono)",fontSize:"0.68rem",color:"var(--text)",outline:"none"}}
                          placeholder="Search bills by number, title, position…"
                          value={billSearch}
                          onChange={e => setBillSearch(e.target.value)}
                        />
                        <button className="cp-card-action" onClick={()=>setShowAddBill(true)}>+ Track a Bill</button>
                      </div>
                      <div className="cp-card">
                        <div className="cp-card-head"><div className="cp-card-title">{billSearch ? `${filteredBills.length} of ${bills.length} bills` : `All Tracked Bills (${bills.length})`}</div></div>
                        {filteredBills.length===0 ? <div className="cp-empty"><div className="cp-empty-icon">📋</div><div className="cp-empty-text">{billSearch ? "No bills match your search." : "No bills tracked for this client yet"}</div></div> :
                          filteredBills.map(b => {
                            const ps = POSITION_STYLES[b.position]||POSITION_STYLES.neutral;
                            const moved = ["In Progress","Passed","PA Signed"].includes(displayStatus(b.status));
                            return (
                              <div key={b.id} className="cp-bill-row">
                                <div className="cp-bill-num">{b.bill_number}</div>
                                <div className="cp-bill-info">
                                  <div className="cp-bill-title">{b.title}</div>
                                  {b.nickname && <div className="cp-bill-nickname">"{b.nickname}"</div>}
                                  {b.notes && <div className="cp-bill-notes">{b.notes}</div>}
                                  <div className="cp-bill-meta">{displayStatus(b.status)} · Added {b.added_at?new Date(b.added_at).toLocaleDateString("en-US",{month:"short",day:"numeric"}):""}</div>
                                </div>
                                <span className={`pos-tag ${ps.cls}`}>{ps.label}</span>
                                {moved && <span className="moved-tag">▲ ACTIVE</span>}
                                {/* MOVED badge: action since client was last opened */}
                                {client?.last_opened_at && b.last_action_date && new Date(b.last_action_date) > new Date(client.last_opened_at) && (
                                  <span className="cp-moved-badge">MOVED</span>
                                )}
                                <div className="cp-row-actions">
                                  <button className="cp-row-btn" onClick={()=>setEditBill(b)}>Edit</button>
                                  <button className="cp-row-btn del" onClick={()=>deleteBill(b.id)}>✕</button>
                                </div>
                              </div>
                            );
                          })
                        }
                      </div>
                    </div>
                    );
                  })()}

                  {/* MEETINGS TAB */}
                  {tab==="meetings" && (
                    <div>
                      <div style={{display:"flex",justifyContent:"flex-end",marginBottom:"0.85rem"}}>
                        <button className="cp-card-action" onClick={()=>setShowAddMeeting(true)}>+ Add Meeting</button>
                      </div>
                      {upcomingMeetings.length > 0 && (
                        <div className="cp-card" style={{marginBottom:"1rem"}}>
                          <div className="cp-card-head"><div className="cp-card-title">Upcoming ({upcomingMeetings.length})</div></div>
                          {upcomingMeetings.map(m => {
                            const d = m.meeting_date ? new Date(m.meeting_date) : null;
                            return (
                              <div key={m.id} className="cp-meeting-row">
                                <div className="cp-meeting-date">
                                  <div className="cp-meeting-month">{d?.toLocaleDateString("en-US",{month:"short"})||"TBD"}</div>
                                  <div className="cp-meeting-day">{d?.getDate()||"?"}</div>
                                </div>
                                <div className="cp-meeting-info">
                                  <div className="cp-meeting-title">{m.title}</div>
                                  <div className="cp-meeting-sub">{m.location}{m.attendees?` · ${m.attendees}`:""}{m.legislators?` · ${m.legislators}`:""}</div>
                                  {m.notes && <div style={{fontFamily:"var(--font-mono)",fontSize:"0.58rem",color:"var(--muted)",marginTop:"0.2rem"}}>{m.notes.slice(0,120)}{m.notes.length>120?"…":""}</div>}
                                  {m.follow_up && <div className="cp-meeting-followup">↳ {m.follow_up}{m.follow_up_due?` (due ${m.follow_up_due})`:""}</div>}
                                </div>
                                <div className="cp-row-actions" style={{alignSelf:"flex-start",paddingTop:"2px"}}>
                                  <button className="cp-row-btn" onClick={()=>setEditMeeting(m)}>Edit</button>
                                  <button className="cp-row-btn del" onClick={()=>deleteMeeting(m.id)}>✕</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {pastMeetings.length > 0 && (
                        <div className="cp-card">
                          <div className="cp-card-head"><div className="cp-card-title">Past Meetings ({pastMeetings.length})</div></div>
                          {pastMeetings.map(m => {
                            const d = m.meeting_date ? new Date(m.meeting_date) : null;
                            return (
                              <div key={m.id} className="cp-meeting-row cp-meeting-past">
                                <div className="cp-meeting-date">
                                  <div className="cp-meeting-month">{d?.toLocaleDateString("en-US",{month:"short"})||"TBD"}</div>
                                  <div className="cp-meeting-day">{d?.getDate()||"?"}</div>
                                </div>
                                <div className="cp-meeting-info">
                                  <div className="cp-meeting-title">{m.title}</div>
                                  <div className="cp-meeting-sub">{m.location}{m.attendees?` · ${m.attendees}`:""}</div>
                                  {m.notes && <div style={{fontFamily:"var(--font-mono)",fontSize:"0.58rem",color:"var(--muted)",marginTop:"0.2rem"}}>{m.notes.slice(0,120)}{m.notes.length>120?"…":""}</div>}
                                </div>
                                <div className="cp-row-actions" style={{alignSelf:"flex-start",paddingTop:"2px"}}>
                                  <button className="cp-row-btn" onClick={()=>setEditMeeting(m)}>Edit</button>
                                  <button className="cp-row-btn del" onClick={()=>deleteMeeting(m.id)}>✕</button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {meetings.length===0 && <div className="cp-empty"><div className="cp-empty-icon">📅</div><div className="cp-empty-text">No meetings logged yet</div></div>}
                    </div>
                  )}

                  {/* ACTIVITY TAB */}
                  {tab==="activity" && (
                    <div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.85rem"}}>
                        <div style={{fontFamily:"var(--font-mono)",fontSize:"0.65rem",color:"var(--muted)"}}>Total: <span style={{color:"var(--gold)",fontWeight:600}}>{fmtHours(totalHours)} hrs</span></div>
                        <button className="cp-card-action" onClick={()=>setShowAddActivity(true)}>+ Log Activity</button>
                      </div>
                      <div className="cp-card">
                        <div className="cp-card-head"><div className="cp-card-title">Activity Log ({activity.length})</div></div>
                        {activity.length===0 ? <div className="cp-empty"><div className="cp-empty-icon">📝</div><div className="cp-empty-text">No activity logged yet</div></div> :
                          activity.map(a => (
                            <div key={a.id} className="cp-act-row">
                              <div className={`cp-act-icon ${ACT_CLS[a.type]||"act-meeting"}`}>{ACT_ICONS[a.type]||"📌"}</div>
                              <div className="cp-act-info">
                                <div className="cp-act-desc">{a.description}</div>
                                <div className="cp-act-meta">{a.date?new Date(a.date).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"}):""} · {a.type}</div>
                              </div>
                              {a.hours ? <div className="cp-act-hrs">{fmtHours(a.hours)}h</div> : null}
                              <div className="cp-row-actions">
                                <button className="cp-row-btn" onClick={()=>setEditActivity(a)}>Edit</button>
                                <button className="cp-row-btn del" onClick={()=>deleteActivity(a.id)}>✕</button>
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )}

                  {/* NOTES TAB */}
                  {tab==="notes" && (
                    <div>
                      <div className="cp-card cp-full" style={{marginBottom:"1rem"}}>
                        <div className="cp-card-head"><div className="cp-card-title">Add a Note</div></div>
                        <div className="cp-notes-tab">
                          <div className="cp-add-note-area">
                            <textarea
                              value={newNoteText}
                              onChange={e=>setNewNoteText(e.target.value)}
                              placeholder="Add a note about this client — strategy, context, follow-ups…"
                            />
                            <div className="cp-add-note-foot">
                              <button className="btn-save" onClick={addNote} disabled={!newNoteText.trim()}>Add Note</button>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="cp-card cp-full">
                        <div className="cp-card-head"><div className="cp-card-title">Notes ({notes.length})</div></div>
                        {notes.length===0 ? (
                          <div className="cp-empty"><div className="cp-empty-icon">📝</div><div className="cp-empty-text">No notes yet.</div></div>
                        ) : (
                          <div className="cp-notes-tab">
                            {notes.map(n => (
                              <div key={n.id} className="cp-note-entry">
                                <div className="cp-note-date">{new Date(n.created_at).toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</div>
                                {editNoteId===n.id ? (
                                  <>
                                    <textarea className="cp-textarea" value={editNoteText} onChange={e=>setEditNoteText(e.target.value)} style={{width:"100%",minHeight:"70px"}}/>
                                    <div className="cp-note-actions">
                                      <button className="btn-save" style={{fontSize:"0.6rem",padding:"0.3rem 0.75rem"}} onClick={()=>saveNoteEdit(n.id)}>Save</button>
                                      <button className="btn-cancel" style={{fontSize:"0.6rem",padding:"0.3rem 0.75rem"}} onClick={()=>{setEditNoteId(null);setEditNoteText("");}}>Cancel</button>
                                    </div>
                                  </>
                                ) : (
                                  <>
                                    <div className="cp-note-text">{n.text}</div>
                                    <div className="cp-note-actions">
                                      <button className="cp-row-btn" onClick={()=>{setEditNoteId(n.id);setEditNoteText(n.text);}}>Edit</button>
                                      <button className="cp-row-btn del" onClick={()=>deleteNote(n.id)}>Delete</button>
                                    </div>
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* REPORTS TAB */}
                  {tab==="reports" && (() => {
                    const applyDateFilter = (items, dateField) => {
                      if (!reportFrom && !reportTo) return items;
                      return items.filter(item => {
                        const d = item[dateField] ? new Date(item[dateField]) : null;
                        if (!d) return false;
                        if (reportFrom && d < new Date(reportFrom)) return false;
                        if (reportTo && d > new Date(reportTo + "T23:59:59")) return false;
                        return true;
                      });
                    };
                    const filteredMtgs  = applyDateFilter(meetings, "meeting_date");
                    const filteredAct   = applyDateFilter(activity, "date");
                    const filteredHours = filteredAct.reduce((s,a) => s + (parseFloat(a.hours)||0), 0);
                    const hasRange      = reportFrom || reportTo;
                    const periodLabel   = hasRange
                      ? ((reportFrom || "All time") + " to " + (reportTo || "present"))
                      : new Date().toLocaleDateString("en-US",{month:"long",year:"numeric"});
                    return (
                      <div>
                        <div className="cp-card" style={{marginBottom:"1rem"}}>
                          <div className="cp-card-head">
                            <div className="cp-card-title">Report Date Range</div>
                            {hasRange && (
                              <button className="cp-card-action" style={{color:"var(--muted)"}} onClick={()=>{setReportFrom("");setReportTo("");}}>Clear ✕</button>
                            )}
                          </div>
                          <div style={{padding:"0.85rem 1.1rem",display:"flex",alignItems:"center",gap:"0.75rem",flexWrap:"wrap"}}>
                            <div style={{display:"flex",flexDirection:"column",gap:"0.25rem"}}>
                              <div style={{fontFamily:"var(--font-mono)",fontSize:"0.58rem",color:"var(--muted)",letterSpacing:"0.08em",textTransform:"uppercase"}}>From</div>
                              <input type="date" className="cp-input" style={{width:"160px"}} value={reportFrom} onChange={e=>setReportFrom(e.target.value)}/>
                            </div>
                            <div style={{fontFamily:"var(--font-mono)",fontSize:"0.75rem",color:"var(--muted)",marginTop:"1.1rem"}}>→</div>
                            <div style={{display:"flex",flexDirection:"column",gap:"0.25rem"}}>
                              <div style={{fontFamily:"var(--font-mono)",fontSize:"0.58rem",color:"var(--muted)",letterSpacing:"0.08em",textTransform:"uppercase"}}>To</div>
                              <input type="date" className="cp-input" style={{width:"160px"}} value={reportTo} onChange={e=>setReportTo(e.target.value)}/>
                            </div>
                            {hasRange && (
                              <div style={{fontFamily:"var(--font-mono)",fontSize:"0.65rem",color:"var(--gold)",marginTop:"1.1rem",padding:"0.35rem 0.75rem",background:"rgba(201,168,76,0.06)",border:"1px solid var(--gold-dim)",borderRadius:"6px"}}>
                                {filteredMtgs.length} meetings · {filteredAct.length} activities · {fmtHours(filteredHours)}h
                              </div>
                            )}
                          </div>
                          {hasRange && (
                            <div style={{padding:"0 1.1rem 0.85rem",fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--muted)"}}>
                              Meetings and activity filtered to selected range. Bills always show all tracked legislation.
                            </div>
                          )}
                        </div>
                        <div className="cp-report-card cp-full">
                          <div className="cp-report-icon">📋</div>
                          <div className="cp-report-info">
                            <div className="cp-report-title">Generate Report — {client.name}</div>
                            <div className="cp-report-desc">
                              {hasRange
                                ? `Custom range: ${periodLabel} · ${filteredMtgs.length} meetings · ${filteredAct.length} activities · ${fmtHours(filteredHours)} hours`
                                : `Full history · ${bills.length} bills · ${meetings.length} meetings · ${fmtHours(totalHours)} hours`
                              }<br/>AI executive summary + bill status + meeting recap + activity log. Opens print dialog for PDF export.
                            </div>
                          </div>
                          <button className="cp-report-btn" onClick={async () => {
                            setGenerating(true); setGenStatus("Gathering data…");
                            const win = window.open("", "_blank");
                            if (!win) { alert("Please allow popups for this site."); setGenerating(false); return; }
                            win.document.write("<html><body style='font-family:sans-serif;padding:2rem'>Generating report…</body></html>");
                            try {
                              const bSum = bills.map(b => b.bill_number + (b.nickname?" ("+b.nickname+")":"") + " (" + (b.position||"").toUpperCase() + "): " + b.title + " — " + displayStatus(b.status)).join("\n");
                              const mSum = filteredMtgs.slice(0,10).map(m => (m.meeting_date ? new Date(m.meeting_date).toLocaleDateString("en-US",{month:"short",day:"numeric"}) : "Date TBD") + ": " + m.title + (m.notes ? " — " + m.notes.slice(0,100) : "")).join("\n");
                              const aSum = filteredAct.slice(0,15).map(a => a.date + ": [" + a.type + "] " + a.description + " (" + (a.hours||0) + "hrs)").join("\n");
                              setGenStatus("Generating AI summary…");
                              const prompt = "You are a professional government affairs lobbyist preparing a formal client activity report.\n\nClient: " + client.name + "\nIndustry: " + (client.industry||"N/A") + "\nReporting Period: " + periodLabel + "\nTotal Hours Logged: " + fmtHours(filteredHours) + "\n\nTRACKED LEGISLATION:\n" + (bSum||"No bills tracked.") + "\n\nMEETINGS:\n" + (mSum||"No meetings logged.") + "\n\nACTIVITY LOG:\n" + (aSum||"No activity logged.") + "\n\nWrite a professional 3-paragraph executive summary. Paragraph 1: legislative activity overview. Paragraph 2: wins, progress, notable meetings. Paragraph 3: upcoming priorities and recommendations. Factual, professional, outcome-focused. No bullet points.";
                              const res = await fetch(SERVER + "/api/chat", {method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({model:"claude-sonnet-4-20250514",max_tokens:1000,messages:[{role:"user",content:prompt}]})});
                              const data = await res.json();
                              const summary = data.content?.[0]?.text || "Unable to generate summary.";
                              setGenStatus("Building PDF…");
                              const html = buildReportHTML(client, bills, filteredMtgs, filteredAct, summary, filteredHours);
                              win.document.open(); win.document.write(html); win.document.close();
                              setTimeout(() => win.print(), 800);
                            } catch(e) { console.error(e); }
                            setGenerating(false);
                          }} disabled={generating}>{generating ? genStatus : "⬇ Generate Report"}</button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* MODALS */}
      {(showAddClient||editClient) && <ClientModal client={editClient} onClose={()=>{setShowAddClient(false);setEditClient(null);}} onSave={saveClient} onDelete={editClient ? ()=>{ setShowAddClient(false); setEditClient(null); deleteClient(editClient.id); } : undefined}/>}
      {(showAddMeeting||editMeeting) && <MeetingModal clientId={selected} meeting={editMeeting} onClose={()=>{setShowAddMeeting(false);setEditMeeting(null);}} onSave={saveMeeting}/>}
      {(showAddActivity||editActivity) && <ActivityModal clientId={selected} activity={editActivity} onClose={()=>{setShowAddActivity(false);setEditActivity(null);}} onSave={saveActivity}/>}
      {(showAddBill||editBill) && <BillModal clientId={selected} bill={editBill} onClose={()=>{setShowAddBill(false);setEditBill(null);}} onSave={saveBill}/>}

      {/* BILL DETAIL MODAL */}
      {selectedCpBill && (
        <div className="cp-detail-overlay" onClick={() => setSelectedCpBill(null)}>
          <div className="cp-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="cp-detail-head">
              <div className="cp-detail-number">{selectedCpBill.bill_number}</div>
              <div className="cp-detail-title">{selectedCpBill.nickname || selectedCpBill.title}</div>
              <div style={{display:"flex",gap:"0.5rem",flexWrap:"wrap",marginTop:"0.5rem"}}>
                {selectedCpBill.status && <span style={{background:"rgba(201,168,76,0.08)",border:"1px solid rgba(201,168,76,0.2)",color:"var(--gold)",fontFamily:"var(--font-mono)",fontSize:"0.62rem",padding:"0.2rem 0.6rem",borderRadius:"6px"}}>{displayStatus(selectedCpBill.status)}</span>}
                {selectedCpBill.position && <span style={{background:"var(--surface2)",border:"1px solid var(--border)",color:"var(--muted)",fontFamily:"var(--font-mono)",fontSize:"0.62rem",padding:"0.2rem 0.6rem",borderRadius:"6px"}}>{selectedCpBill.position}</span>}
              </div>
            </div>
            <div className="cp-detail-body">
              {selectedCpBill.nickname && selectedCpBill.title && (
                <div className="cp-detail-row">
                  <div className="cp-detail-label">Full Title</div>
                  <div className="cp-detail-value">{selectedCpBill.title}</div>
                </div>
              )}
              <div className="cp-detail-row">
                <div className="cp-detail-label">Last Action</div>
                <div className="cp-detail-value">{selectedCpBill.last_action || "—"}</div>
              </div>
              {selectedCpBill.last_action_date && (
                <div className="cp-detail-row">
                  <div className="cp-detail-label">Date</div>
                  <div className="cp-detail-value">{new Date(selectedCpBill.last_action_date).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}</div>
                </div>
              )}
              {selectedCpBill.notes && (
                <div className="cp-detail-row">
                  <div className="cp-detail-label">Notes</div>
                  <div className="cp-detail-value">{selectedCpBill.notes}</div>
                </div>
              )}
            </div>
            <div className="cp-detail-foot">
              <button className="cp-detail-action" style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"6px",padding:"0.4rem 0.9rem",fontFamily:"var(--font-mono)",fontSize:"0.65rem",color:"var(--muted)",cursor:"pointer",transition:"all 0.15s"}} onClick={() => { sessionStorage.setItem("wc_search_query", selectedCpBill.bill_number); setSelectedCpBill(null); onNavigate("billtracker"); }}>View in Bill Search →</button>
              <button style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",padding:"0.45rem 1.1rem",fontFamily:"var(--font-mono)",fontSize:"0.68rem",color:"var(--muted)",cursor:"pointer"}} onClick={() => setSelectedCpBill(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* MEETING DETAIL MODAL */}
      {selectedCpMtg && (
        <div className="cp-detail-overlay" onClick={() => setSelectedCpMtg(null)}>
          <div className="cp-detail-modal" onClick={e => e.stopPropagation()}>
            <div className="cp-detail-head">
              <div className="cp-detail-number">MEETING</div>
              <div className="cp-detail-title">{selectedCpMtg.title}</div>
            </div>
            <div className="cp-detail-body">
              {selectedCpMtg.meeting_date && (
                <div className="cp-detail-row">
                  <div className="cp-detail-label">Date</div>
                  <div className="cp-detail-value">{new Date(selectedCpMtg.meeting_date).toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric",year:"numeric"})}</div>
                </div>
              )}
              {selectedCpMtg.location && (
                <div className="cp-detail-row">
                  <div className="cp-detail-label">Location</div>
                  <div className="cp-detail-value">{selectedCpMtg.location}</div>
                </div>
              )}
              {selectedCpMtg.legislators && (
                <div className="cp-detail-row">
                  <div className="cp-detail-label">Legislators / Attendees</div>
                  <div className="cp-detail-value">{selectedCpMtg.legislators}</div>
                </div>
              )}
              {selectedCpMtg.notes && (
                <div className="cp-detail-row">
                  <div className="cp-detail-label">Notes</div>
                  <div className="cp-detail-value">{selectedCpMtg.notes}</div>
                </div>
              )}
              {selectedCpMtg.follow_up && (
                <div className="cp-detail-row">
                  <div className="cp-detail-label">Follow-up</div>
                  <div className="cp-detail-value">{selectedCpMtg.follow_up}</div>
                </div>
              )}
            </div>
            <div className="cp-detail-foot">
              <button className="cp-detail-action" style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"6px",padding:"0.4rem 0.9rem",fontFamily:"var(--font-mono)",fontSize:"0.65rem",color:"var(--muted)",cursor:"pointer",transition:"all 0.15s"}} onClick={() => { const t = selectedCpMtg.title.replace(/^(Committee Meeting|Fundraiser)\s*[—-]\s*/i, "").trim(); sessionStorage.setItem("wc_calendar_meeting_title", t); setSelectedCpMtg(null); onNavigate("calendar"); }}>View in Calendar →</button>
              <button style={{background:"transparent",border:"1px solid var(--border)",borderRadius:"8px",padding:"0.45rem 1.1rem",fontFamily:"var(--font-mono)",fontSize:"0.68rem",color:"var(--muted)",cursor:"pointer"}} onClick={() => setSelectedCpMtg(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* GENERATING OVERLAY */}
      {generating && (
        <div className="gen-overlay">
          <div className="gen-title">Generating Report</div>
          <div className="gen-sub">{genStatus}</div>
          <div className="gen-dots">
            <div className="gen-dot"/><div className="gen-dot"/><div className="gen-dot"/>
          </div>
        </div>
      )}
      {confirmState && <ConfirmDialog message={confirmState.message} subtext={confirmState.subtext} onConfirm={confirmState.onConfirm} onCancel={() => setConfirmState(null)} />}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </>
  );
}
